import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import {
  AppWindow, Bell, Bookmark, Check, ChevronRight, Clock3, Code2, Copy, CreditCard,
  Eye, Gamepad2, Heart, History, Home, ImagePlus, Layers3, Library, LogOut, Maximize2,
  Menu, MessageCircle, Moon, MoreHorizontal, PenLine, Play, Plus, Repeat2,
  RefreshCw, Rocket, Search, Settings, Share2, Shield, Sparkles, Sun, User, Video, WandSparkles,
  X, Zap,
} from 'lucide-react'
import './App.css'
import { isGuestLike, rankPersonalizedFeed, rankShortsFeed, rankTrendingApps, rankTrendingTopics, rankWhoToFollow } from './discovery'
import { appyAdapter } from './appy-adapter'
import type { AdminBannedUser, AdminPostRow, AdminReport, AdminStats, AppKind, AppyCreation, AppyUser, Intelligence } from './model'
import { AppyLogo } from './logo'
import { countWords, featuresForPlan, formatBytes, type PlanFeatures } from './plan-features'
import { renderRichText } from './rich-text'
import { useAppyStore } from './use-appy-store'

type View = 'home' | 'explore' | 'shorts' | 'notifications' | 'library' | 'builds' | 'profile' | 'settings' | 'billing' | 'admin'
type FeedMode = 'for-you' | 'following'
type NotificationFilter = 'all' | 'builds' | 'social' | 'updates'
type LibraryTab = 'saved' | 'recent' | 'created' | 'collections'
type BuildTab = 'all' | 'building' | 'ready' | 'published'
type ProfileTab = 'posts' | 'apps' | 'reposts' | 'replies' | 'likes'

const COMPANY_NAME = 'Starex'
const APPY_DESKTOP_URL = 'https://appy-6kra.onrender.com'

const compact = (value: number) => new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value)
const relative = (stamp: string) => {
  const hours = Math.max(0, Math.round((Date.now() - new Date(stamp).getTime()) / 3_600_000))
  if (hours < 1) return 'now'
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

/** Builder UI is desktop-only — phones get a clear redirect instead of a broken layout. */
function useIsMobileBuilder(maxWidth = 900) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia(`(max-width: ${maxWidth}px)`).matches
  })
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`)
    const update = () => setIsMobile(mq.matches)
    update()
    mq.addEventListener?.('change', update)
    // Safari < 14
    mq.addListener?.(update)
    return () => {
      mq.removeEventListener?.('change', update)
      mq.removeListener?.(update)
    }
  }, [maxWidth])
  return isMobile
}

function DesktopBuilderNotice({ onClose, compact = false }: { onClose?: () => void; compact?: boolean }) {
  return (
    <div className={`desktop-builder-notice ${compact ? 'desktop-builder-notice-compact' : ''}`} role="status">
      {onClose && (
        <button type="button" className="icon-button desktop-builder-close" aria-label="Close" onClick={onClose}>
          <X />
        </button>
      )}
      <div className="desktop-builder-mark"><AppWindow /></div>
      <h2>Build on a computer</h2>
      <p>To make apps on this platform please open the app on a computer</p>
      <a className="primary-button desktop-builder-link" href={APPY_DESKTOP_URL} target="_blank" rel="noopener noreferrer">
        {APPY_DESKTOP_URL}
      </a>
      <small>Made by {COMPANY_NAME}</small>
    </div>
  )
}
const formatHour = (hour: number) => new Intl.DateTimeFormat('en', { hour: 'numeric', minute: '2-digit' }).format(new Date(2026, 0, 1, hour))
const errorText = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return fallback
}
const missingUser = (id: string): AppyUser => ({ id, name: 'Unknown creator', handle: `creator_${id.replace(/-/g, '').slice(0, 8) || 'unknown'}`, bio: 'This creator profile is unavailable.', avatar: '?', followers: 0, following: 0 })
const formatCredits = (billing: { creditBalance: number; unlimited?: boolean }, digits = 2) =>
  billing.unlimited ? '∞' : `$${billing.creditBalance.toFixed(digits)}`

function Brand({ small = false }: { small?: boolean }) {
  return (
    <div className={`brand ${small ? 'brand-small' : ''}`}>
      <AppyLogo />
      <span>Appy</span>
    </div>
  )
}

function LoadingScreen() {
  return (
    <div className="loading-screen">
      <div className="loading-mark"><AppyLogo decorative /></div>
      <h1>Connecting to Appy</h1>
      <p>Loading apps, builders, and your library.</p>
      <div className="loading-line"><i /></div>
    </div>
  )
}

function ConnectionError({ detail, onRetry }: { detail: string; onRetry: () => void }) {
  return <main className="connection-error" role="alert"><Brand /><div className="connection-error-mark"><RefreshCw /></div><h1>Appy could not connect</h1><p>Your account, credits, and subscription were not replaced with demo data.</p><small>{detail}</small><button className="primary-button" onClick={onRetry}><RefreshCw />Try again</button></main>
}

function Toast({ text, onClose }: { text: string; onClose: () => void }) {
  useEffect(() => { const id = window.setTimeout(onClose, 2600); return () => window.clearTimeout(id) }, [onClose])
  return <button className="toast" onClick={onClose}><Check size={16} />{text}</button>
}

function Modal({ children, onClose, wide = false, label }: { children: React.ReactNode; onClose: () => void; wide?: boolean; label?: string }) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [onClose])
  return <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><section className={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={label}>{children}</section></div>
}

function UserAvatar({ user, size = 'md', onClick }: { user: AppyUser; size?: 'sm' | 'md' | 'lg'; onClick?: () => void }) {
  const content = user.avatarUrl
    ? <img src={user.avatarUrl} alt="" className="avatar-image" />
    : user.avatar
  if (onClick) return <button type="button" className={`avatar avatar-${size} avatar-button`} aria-label={`${user.name}'s profile`} onClick={onClick}>{content}</button>
  return <span className={`avatar avatar-${size}`} aria-label={`${user.name}'s avatar`}>{content}</span>
}

/** Appy logo for staff + purple check for verified subscribers. */
function UserBadges({ user }: { user: AppyUser }) {
  return (
    <span className="user-badges">
      {user.staffRole && (
        <span
          className="staff-badge"
          title={user.staffRole === 'owner' ? 'Appy owner' : 'Appy moderator'}
          aria-label={user.staffRole === 'owner' ? 'Appy owner' : 'Appy moderator'}
        >
          <AppyLogo decorative />
        </span>
      )}
      {user.verified && (
        <span className="verified verified-purple" title={user.staffRole ? 'Verified staff' : 'Verified subscriber'}>✓</span>
      )}
    </span>
  )
}

/** Styled upload control — never shows the native browser file button. */
function ImageUploadField({
  label,
  buttonLabel,
  preview,
  previewClassName,
  onPick,
  compact = false,
}: {
  label: string
  buttonLabel: string
  preview?: string | null
  previewClassName?: string
  onPick: (file: File) => void | Promise<void>
  compact?: boolean
}) {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <div className={`image-upload-field ${compact ? 'image-upload-compact' : ''}`}>
      <div className="image-upload-copy">
        <span className="image-upload-label">{label}</span>
        <button
          type="button"
          className="secondary-button small image-upload-trigger"
          onClick={() => inputRef.current?.click()}
        >
          <ImagePlus />
          {buttonLabel}
        </button>
      </div>
      {preview ? <img src={preview} alt="" className={previewClassName ?? 'cover-thumb'} /> : (
        <button type="button" className="image-upload-dropzone" onClick={() => inputRef.current?.click()}>
          <ImagePlus />
          <span>Click to choose an image</span>
          <small>JPG, PNG, WEBP · up to 8MB</small>
        </button>
      )}
      <input
        id={inputId}
        ref={inputRef}
        className="sr-only-file"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          if (file) void onPick(file)
        }}
      />
    </div>
  )
}

function AppPreview({ app, interactive = false, className = '' }: { app: AppyCreation; interactive?: boolean; className?: string }) {
  if (app.postType === 'short') {
    if (app.videoUrl) {
      return (
        <div className={`app-preview short-preview ${className}`}>
          <video src={app.videoUrl} className="post-video-thumb" muted playsInline preload="metadata" />
          <span className="short-badge"><Play fill="currentColor" size={14} />Short</span>
        </div>
      )
    }
    return null
  }
  if (app.postType === 'text') {
    if (app.mediaUrl) return <div className={`app-preview text-preview ${className}`}><img src={app.mediaUrl} alt="" className="post-media" /></div>
    if (app.videoUrl) return <div className={`app-preview text-preview ${className}`}><video src={app.videoUrl} className="post-media" controls playsInline preload="metadata" /></div>
    return null
  }
  if (app.status === 'building') {
    const latest = app.buildLog?.[app.buildLog.length - 1]
    return (
      <div className={`app-preview building-preview ${className}`}>
        <Sparkles size={30} />
        <strong>Building {app.kind === 'game' ? 'Game' : 'App'}</strong>
        <span className="build-live-status">{latest?.file ? <><code>{latest.file}</code> · {latest.message}</> : (latest?.message ?? 'Planning · Building · Testing')}</span>
        <div className="build-log-mini">
          {(app.buildLog ?? []).slice(-4).map((entry, index) => (
            <small key={`${entry.at}-${index}`}>{entry.file ? `${entry.file}: ` : ''}{entry.message}</small>
          ))}
        </div>
        <div className="build-sweep" />
      </div>
    )
  }
  if (!app.srcDoc) return <div className={`app-preview unavailable-preview ${className}`}><Code2 size={28} /><strong>Preview unavailable</strong><span>This build can be retried.</span></div>
  if (app.coverUrl && !interactive) {
    return <div className={`app-preview cover-image-preview ${className}`} style={{ backgroundImage: `url(${app.coverUrl})` }}><span className="preview-kind">{app.kind === 'game' ? <Gamepad2 /> : <AppWindow />}{app.kind}</span><div><small>Built with Appy</small><strong>{app.title}</strong></div><span className="preview-play" aria-hidden="true"><Play fill="currentColor" /></span></div>
  }
  return <div className={`app-preview ${app.coverClass} ${className}`}>
    {interactive
      ? (
        <iframe
          key={`${app.id}-v${app.version}-${app.srcDoc.length}-${app.updatedAt}`}
          title={`${app.title} interactive preview`}
          data-appy-app-id={app.id}
          sandbox="allow-scripts allow-forms allow-pointer-lock"
          allow="fullscreen; gamepad"
          srcDoc={app.srcDoc}
        />
      )
      : <div className="preview-poster"><span className="preview-kind">{app.kind === 'game' ? <Gamepad2 /> : <AppWindow />}{app.kind}</span><div><small>Built with Appy</small><strong>{app.title}</strong><p>{app.tags.slice(0, 2).join(' · ')}</p></div><span className="preview-play" aria-hidden="true"><Play fill="currentColor" /></span></div>}
  </div>
}

interface CardProps {
  app: AppyCreation
  author: AppyUser
  currentUserId: string
  onLike: () => void
  onSave: () => void
  onRepost: () => void
  onOpen: () => void
  onComments: () => void
  onFollow: () => void
  onEditor: () => void
  onShare: () => void
  onProfile: () => void
  onEditPost?: () => void
  onDeletePost?: () => void
  onReport?: () => void
  onMention?: (handle: string) => void
  onHashtag?: (tag: string) => void
  onDwell?: (dwellMs: number, opened: boolean) => void
  onOpenShort?: () => void
}

function AppCard(props: CardProps) {
  const { app, author } = props
  const isOwner = app.authorId === props.currentUserId
  const isText = app.postType === 'text'
  const isShort = app.postType === 'short'
  const isSocial = isText || isShort
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLElement>(null)
  const openedRef = useRef(false)
  useEffect(() => {
    if (!menuOpen) return
    const close = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false)
    }
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setMenuOpen(false) }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', escape)
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', escape) }
  }, [menuOpen])
  useEffect(() => {
    if (!props.onDwell || !cardRef.current) return
    if (typeof IntersectionObserver === 'undefined') return
    let visibleSince: number | null = null
    let total = 0
    const flush = () => {
      if (visibleSince != null) {
        total += Date.now() - visibleSince
        visibleSince = null
      }
      if (total >= 400) {
        props.onDwell?.(total, openedRef.current)
        total = 0
      }
    }
    const io = new IntersectionObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
        visibleSince = Date.now()
      } else {
        flush()
      }
    }, { threshold: [0, 0.45, 0.75] })
    io.observe(cardRef.current)
    return () => {
      flush()
      io.disconnect()
    }
  }, [props.onDwell, app.id])
  const richHandlers = { onMention: props.onMention, onHashtag: props.onHashtag }
  const hasMedia = isShort ? Boolean(app.videoUrl) : isText ? Boolean(app.mediaUrl || app.videoUrl) : true
  return <article ref={cardRef} className={`post-card ${isShort ? 'post-card-short' : ''}`} data-testid={`post-${app.id}`}>
    <div className="post-avatar"><UserAvatar user={author} onClick={props.onProfile} /></div>
    <div className="post-body">
      <header className="post-meta"><button className="name-link" onClick={props.onProfile}><b>{author.name}</b><UserBadges user={author} /><span>@{author.handle} · {relative(app.publishedAt ?? app.createdAt)}</span></button>{isShort && <span className="post-kind-pill">Short</span>}{author.id !== props.currentUserId && <button className={`follow-inline ${author.followedByMe ? 'following' : ''}`} onClick={props.onFollow}>{author.followedByMe ? 'Following' : 'Follow'}</button>}<div className="post-menu-wrap" ref={menuRef}><button className="icon-button" aria-label="More actions" aria-expanded={menuOpen} aria-haspopup="menu" onClick={() => setMenuOpen((open) => !open)}><MoreHorizontal /></button>{menuOpen && <div className="post-menu" role="menu"><button role="menuitem" onClick={() => { setMenuOpen(false); props.onShare() }}><Copy />Copy link</button>{!isSocial && <button role="menuitem" onClick={() => { setMenuOpen(false); props.onSave() }}><Bookmark fill={app.savedByMe ? 'currentColor' : 'none'} />{app.savedByMe ? 'Remove from Library' : 'Save to Library'}</button>}<button role="menuitem" onClick={() => { setMenuOpen(false); props.onComments() }}><MessageCircle />View comments</button>{isOwner && app.postId && props.onEditPost && <button role="menuitem" onClick={() => { setMenuOpen(false); props.onEditPost?.() }}><PenLine />Edit post</button>}{!isOwner && props.onReport && <button role="menuitem" onClick={() => { setMenuOpen(false); props.onReport?.() }}><Shield />Report</button>}{app.postId && props.onDeletePost && <button role="menuitem" className="danger-menu" onClick={() => { setMenuOpen(false); props.onDeletePost?.() }}><X />{isOwner ? 'Delete post' : 'Delete as staff'}</button>}</div>}</div></header>
      <div className="post-copy">{!isSocial && <div className="post-title-row"><h2>{app.title}</h2><span>{app.kind}</span>{app.version > 0 && <span>v{app.version}</span>}</div>}<p className="post-body-text">{renderRichText(app.postBody || app.description, richHandlers)}</p></div>
      {hasMedia && <button className={`preview-button ${isShort ? 'preview-button-short' : ''}`} onClick={() => { openedRef.current = true; if (app.status === 'building' && isOwner) props.onEditor(); else if (isShort) (props.onOpenShort ?? props.onOpen)(); else if (isText) props.onComments(); else props.onOpen() }} aria-label={`${app.status === 'building' ? 'Open build' : isShort ? 'Watch' : 'Open'} ${app.title}`}><AppPreview app={app} /></button>}
      {!isSocial && <div className="post-primary-actions"><button onClick={() => { openedRef.current = true; props.onOpen() }} disabled={app.status === 'building'}><Maximize2 />Full screen</button><button className={app.savedByMe ? 'active' : ''} onClick={props.onSave}><Bookmark fill={app.savedByMe ? 'currentColor' : 'none'} />{app.savedByMe ? 'Saved' : 'Save'}</button>{isOwner && <button onClick={props.onEditor}><WandSparkles />Edit app</button>}</div>}
      <footer className="social-row"><button onClick={() => { openedRef.current = true; props.onComments() }} aria-label="Comments"><MessageCircle /><span>{compact(app.comments)}</span></button><button className={app.repostedByMe ? 'reposted' : ''} onClick={props.onRepost} aria-label="Repost"><Repeat2 /><span>{compact(app.reposts)}</span></button><button className={app.likedByMe ? 'liked' : ''} onClick={props.onLike} aria-label="Heart"><Heart fill={app.likedByMe ? 'currentColor' : 'none'} /><span>{compact(app.likes)}</span></button><button onClick={props.onShare} aria-label={`Share ${app.title}`}><Share2 /></button>{!isSocial && <span className="plays" aria-label={`${app.plays} plays`}><Eye />{compact(app.plays)}</span>}</footer>
    </div>
  </article>
}

function EmptyState({ icon, title, body, action, onAction }: { icon: React.ReactNode; title: string; body: string; action?: string; onAction?: () => void }) {
  return <div className="empty-state">{icon}<h2>{title}</h2><p>{body}</p>{action && <button className="primary-button" onClick={onAction}>{action}</button>}</div>
}

function Composer({
  intelligence,
  plan,
  onClose,
  onBuild,
  onTextPost,
  onShortPost,
  onUploadPostMedia,
  onUploadShortMedia,
  onModerateImage,
  onModerateVideo,
}: {
  intelligence: Intelligence
  plan: PlanFeatures
  onClose: () => void
  onBuild: (prompt: string, kind: AppKind, level: Intelligence) => Promise<void>
  onTextPost: (body: string, mediaUrl?: string | null) => Promise<void>
  onShortPost: (body: string, videoUrl: string, durationSeconds?: number | null) => Promise<void>
  onUploadPostMedia: (file: File) => Promise<string>
  onUploadShortMedia: (file: File) => Promise<string>
  onModerateImage: (file: File) => Promise<void>
  onModerateVideo: (file: File) => Promise<void>
}) {
  const isMobile = useIsMobileBuilder()
  const [mode, setMode] = useState<'app' | 'text' | 'short'>('text')
  const [prompt, setPrompt] = useState('')
  const [kind, setKind] = useState<AppKind>('app')
  const [level, setLevel] = useState<Intelligence>(intelligence)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [mediaFile, setMediaFile] = useState<File | null>(null)
  const [mediaPreview, setMediaPreview] = useState<string | null>(null)
  const [videoDuration, setVideoDuration] = useState<number | null>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)
  const estimate = level === 'low' ? '$0.10–$0.45' : level === 'high' ? '$0.50–$1.80' : '$0.25–$0.95'
  const words = countWords(prompt)
  const maxChars = mode === 'app' ? 6000 : plan.maxPostChars
  const overWords = mode !== 'app' && words > plan.maxPostWords
  const overChars = prompt.length > maxChars
  const mobileBlocksBuild = isMobile && mode === 'app'
  const canSubmit = mobileBlocksBuild
    ? false
    : mode === 'short'
      ? Boolean(mediaFile) && !overWords && !overChars && !submitting
      : Boolean(prompt.trim()) && !overWords && !overChars && !submitting

  useEffect(() => () => {
    if (mediaPreview?.startsWith('blob:')) URL.revokeObjectURL(mediaPreview)
  }, [mediaPreview])

  const pickMedia = (file: File, asVideo = false) => {
    setError('')
    if (mediaPreview?.startsWith('blob:')) URL.revokeObjectURL(mediaPreview)
    if (asVideo) {
      if (file.size > plan.maxVideoBytes) {
        setError(`Videos on ${plan.label} can be up to ${formatBytes(plan.maxVideoBytes)}.`)
        return
      }
      const url = URL.createObjectURL(file)
      setMediaFile(file)
      setMediaPreview(url)
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.src = url
      video.onloadedmetadata = () => {
        const seconds = Math.round(video.duration || 0)
        setVideoDuration(seconds || null)
        if (seconds > plan.maxShortSeconds) {
          setError(`Shorts on ${plan.label} can be up to ${plan.maxShortSeconds}s. This clip is ${seconds}s.`)
        }
      }
      return
    }
    if (file.size > plan.maxImageBytes) {
      setError(`Images on ${plan.label} can be up to ${formatBytes(plan.maxImageBytes)}.`)
      return
    }
    setVideoDuration(null)
    setMediaFile(file)
    setMediaPreview(URL.createObjectURL(file))
  }

  const clearMedia = () => {
    if (mediaPreview?.startsWith('blob:')) URL.revokeObjectURL(mediaPreview)
    setMediaFile(null)
    setMediaPreview(null)
    setVideoDuration(null)
  }

  const switchMode = (next: 'app' | 'text' | 'short') => {
    setMode(next)
    setError('')
    if (next !== mode) clearMedia()
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (mobileBlocksBuild) {
      setError('To make apps on this platform please open the app on a computer')
      return
    }
    if (!canSubmit) return
    if (overWords) {
      setError(`${plan.label} allows up to ${plan.maxPostWords.toLocaleString()} words per post. Upgrade for longer posts.`)
      return
    }
    setSubmitting(true)
    setError('')
    try {
      if (mode === 'short') {
        if (!mediaFile) throw new Error('Add a video for your Short.')
        await onModerateVideo(mediaFile)
        const videoUrl = await onUploadShortMedia(mediaFile)
        await onShortPost(prompt, videoUrl, videoDuration)
      } else if (mode === 'text') {
        let mediaUrl: string | null = null
        if (mediaFile) {
          await onModerateImage(mediaFile)
          mediaUrl = await onUploadPostMedia(mediaFile)
        }
        await onTextPost(prompt, mediaUrl)
      } else {
        await onBuild(prompt, kind, level)
      }
      onClose()
    } catch (caught) {
      setError(errorText(caught, mode === 'short' ? 'Could not publish Short.' : mode === 'text' ? 'Could not publish post.' : 'Could not start build.'))
    } finally {
      setSubmitting(false)
    }
  }

  const title = mode === 'short' ? 'New Short' : mode === 'text' ? 'New post' : 'Build an app'
  const submitLabel = submitting
    ? (mode === 'short' ? 'Checking video…' : mode === 'text' ? (mediaFile ? 'Checking image…' : 'Posting…') : 'Starting…')
    : mode === 'short' ? 'Post Short' : mode === 'text' ? 'Post' : `Build ${kind === 'game' ? 'Game' : 'App'}`

  return (
    <Modal onClose={onClose} label={title}>
      <form onSubmit={submit} className={`composer ${mode === 'short' ? 'composer-short' : ''}`}>
        <header>
          <div>
            <span className="eyebrow">Share with Appy · {plan.label}</span>
            <h2>{title}</h2>
          </div>
          <button type="button" className="icon-button" aria-label="Close composer" onClick={onClose}><X /></button>
        </header>
        <div className="composer-scroll">
          <div className="segmented composer-modes">
            <button type="button" className={mode === 'text' ? 'selected' : ''} aria-pressed={mode === 'text'} onClick={() => switchMode('text')}><PenLine />Post</button>
            <button type="button" className={mode === 'short' ? 'selected' : ''} aria-pressed={mode === 'short'} onClick={() => switchMode('short')}><Video />Short</button>
            <button type="button" className={mode === 'app' ? 'selected' : ''} aria-pressed={mode === 'app'} onClick={() => switchMode('app')}><Sparkles />Build</button>
          </div>
          {mobileBlocksBuild ? (
            <DesktopBuilderNotice compact />
          ) : (
          <>
          <label className={`prompt-box ${mode === 'short' ? 'prompt-box-compact' : ''}`}>
            <span className="sr-only">{mode === 'short' ? 'Short caption' : mode === 'text' ? 'Write a post' : 'Describe the app or game'}</span>
            <textarea
              autoFocus
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder={mode === 'short' ? 'Caption your Short… use #tags and @mentions' : mode === 'text' ? 'What’s happening? Use #tags, @mentions, and links…' : 'Describe the app or game you want to make…'}
              maxLength={maxChars}
              rows={mode === 'short' ? 2 : 5}
            />
            <span className={overWords || overChars ? 'limit-warn' : ''}>
              {mode === 'app' ? `${prompt.length}/${maxChars}` : `${words.toLocaleString()} / ${plan.maxPostWords.toLocaleString()} words · ${prompt.length.toLocaleString()} chars`}
            </span>
          </label>
          {mode === 'app' ? (
            <>
              <div className="segmented">
                <button type="button" className={kind === 'app' ? 'selected' : ''} aria-pressed={kind === 'app'} onClick={() => setKind('app')}><AppWindow />App</button>
                <button type="button" className={kind === 'game' ? 'selected' : ''} aria-pressed={kind === 'game'} onClick={() => setKind('game')}><Gamepad2 />Game</button>
              </div>
              <section className="composer-option">
                <div><b>Intelligence</b><span>Controls quality, time, and token usage.</span></div>
                <div className="quality-buttons">{(['low', 'medium', 'high'] as Intelligence[]).map((item) => <button type="button" key={item} className={level === item ? 'selected' : ''} aria-pressed={level === item} onClick={() => setLevel(item)}>{item}</button>)}</div>
              </section>
              <section className="composer-option">
                <div><b>Visibility</b><span>You choose the post caption after the build finishes.</span></div>
                <span className="select-like">Draft first <Check /></span>
              </section>
              <div className="cost-estimate"><Zap /><div><b>Estimated build credits</b><span>{estimate} · actual token usage is charged</span></div></div>
            </>
          ) : mode === 'short' ? (
            <div className="composer-media">
              <p className="modal-intro">Upload a vertical video. Luna reviews it before it goes live. Up to {plan.maxShortSeconds}s · {formatBytes(plan.maxVideoBytes)} on {plan.label}.</p>
              <input
                ref={videoInputRef}
                className="sr-only-file"
                type="file"
                accept="video/mp4,video/webm,video/quicktime"
                onChange={(event) => {
                  const file = event.target.files?.[0]
                  event.target.value = ''
                  if (file) pickMedia(file, true)
                }}
              />
              {mediaPreview ? (
                <div className="composer-video-preview">
                  <video src={mediaPreview} controls playsInline className="short-composer-video" />
                  <div className="composer-video-meta">
                    <span>{videoDuration != null ? `${videoDuration}s` : '…'} · {mediaFile ? formatBytes(mediaFile.size) : ''}</span>
                    <button type="button" className="secondary-button small" onClick={() => videoInputRef.current?.click()}>Change video</button>
                    <button type="button" className="secondary-button small composer-clear-media" onClick={clearMedia}><X />Remove</button>
                  </div>
                </div>
              ) : (
                <button type="button" className="image-upload-dropzone short-dropzone" onClick={() => videoInputRef.current?.click()}>
                  <Video />
                  <span>Choose a Short video</span>
                  <small>MP4, WEBM, MOV · max {plan.maxShortSeconds}s · {formatBytes(plan.maxVideoBytes)}</small>
                </button>
              )}
            </div>
          ) : (
            <div className="composer-media">
              <p className="modal-intro">Write freely — #hashtags, @mentions, and links light up blue. Images up to {formatBytes(plan.maxImageBytes)} on {plan.label}. Luna reviews images before posting.</p>
              <ImageUploadField
                label="Post image"
                buttonLabel={mediaPreview ? 'Change image' : 'Add image'}
                preview={mediaPreview}
                previewClassName="post-media-thumb"
                onPick={(file) => pickMedia(file, false)}
              />
              {mediaPreview && (
                <button type="button" className="secondary-button small composer-clear-media" onClick={clearMedia}>
                  <X />Remove image
                </button>
              )}
            </div>
          )}
          {error && <p className="form-error" role="alert">{error}</p>}
          </>
          )}
        </div>
        <footer>
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          {!mobileBlocksBuild && (
            <button className="primary-button" disabled={!canSubmit}>
              {submitLabel}
              <Sparkles />
            </button>
          )}
        </footer>
      </form>
    </Modal>
  )
}

function CommentsModal({
  app,
  comments,
  users,
  currentUser,
  onClose,
  onAdd,
  onLike,
  onRepost,
  onProfile,
  onOpenHandle,
}: {
  app: AppyCreation
  comments: ReturnType<typeof useAppyStore>['snapshot']['comments']
  users: AppyUser[]
  currentUser: AppyUser
  onClose: () => void
  onAdd: (body: string, parentId?: string | null) => void
  onLike: (id: string) => void
  onRepost: (id: string) => void
  onProfile: (userId: string) => void
  onOpenHandle: (handle: string) => void
}) {
  const REPLY_PAGE = 3
  const [body, setBody] = useState('')
  const [replyTo, setReplyTo] = useState<{ id: string; handle: string } | null>(null)
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [visibleReplies, setVisibleReplies] = useState<Record<string, number>>({})
  const appComments = comments.filter((item) => item.appId === app.id)
  const roots = appComments
    .filter((item) => !item.parentId)
    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
  const repliesByParent = useMemo(() => {
    const map = new Map<string, typeof appComments>()
    for (const comment of appComments) {
      if (!comment.parentId) continue
      const list = map.get(comment.parentId) ?? []
      list.push(comment)
      map.set(comment.parentId, list)
    }
    for (const list of map.values()) list.sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
    return map
  }, [appComments])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!body.trim()) return
    const parentId = replyTo?.id ?? null
    onAdd(body, parentId)
    if (parentId) {
      setExpanded((current) => ({ ...current, [parentId]: true }))
      setVisibleReplies((current) => ({
        ...current,
        [parentId]: Math.max(current[parentId] ?? REPLY_PAGE, (repliesByParent.get(parentId)?.length ?? 0) + 1),
      }))
    }
    setBody('')
    setReplyTo(null)
  }

  const renderMessage = (comment: (typeof appComments)[number], _isReply = false) => {
    const user = users.find((item) => item.id === comment.userId) ?? missingUser(comment.userId)
    const childReplies = repliesByParent.get(comment.id) ?? []
    const replyTotal = Math.max(comment.replyCount ?? 0, childReplies.length)
    const isOpen = Boolean(expanded[comment.id])
    const shown = visibleReplies[comment.id] ?? REPLY_PAGE
    const visible = isOpen ? childReplies.slice(0, shown) : []
    const remaining = Math.max(0, childReplies.length - visible.length)

    return (
      <div key={comment.id} className="chat-thread">
        <article className="chat-message">
          <UserAvatar user={user} size="sm" onClick={() => onProfile(user.id)} />
          <div className="chat-message-body">
            <header className="chat-message-meta">
              <button type="button" className="name-link" onClick={() => onProfile(user.id)}>
                <b>{user.name}</b>
                <UserBadges user={user} />
                <span>@{user.handle} · {relative(comment.createdAt)}</span>
              </button>
            </header>
            <p className="chat-message-text">{renderRichText(comment.body, { onMention: onOpenHandle })}</p>
            <div className="chat-message-actions social-row">
              <button type="button" onClick={() => setReplyTo({ id: comment.id, handle: user.handle })} aria-label="Reply">
                <MessageCircle size={15} /><span>{replyTotal}</span>
              </button>
              <button type="button" className={comment.repostedByMe ? 'reposted' : ''} onClick={() => onRepost(comment.id)} aria-label="Repost comment">
                <Repeat2 size={15} /><span>{compact(comment.reposts ?? 0)}</span>
              </button>
              <button type="button" className={comment.likedByMe ? 'liked' : ''} onClick={() => onLike(comment.id)} aria-label={`${comment.likedByMe ? 'Unlike' : 'Like'} comment`}>
                <Heart size={15} fill={comment.likedByMe ? 'currentColor' : 'none'} /><span>{compact(comment.likes)}</span>
              </button>
            </div>
          </div>
        </article>
        {replyTotal > 0 && !isOpen && (
          <button
            type="button"
            className="show-replies-button"
            onClick={() => {
              setExpanded((current) => ({ ...current, [comment.id]: true }))
              setVisibleReplies((current) => ({ ...current, [comment.id]: current[comment.id] ?? REPLY_PAGE }))
            }}
          >
            Show {replyTotal} {replyTotal === 1 ? 'reply' : 'replies'}
          </button>
        )}
        {isOpen && childReplies.length > 0 && (
          <div className="chat-replies-flat">
            {visible.map((reply) => renderMessage(reply))}
            {remaining > 0 ? (
              <button
                type="button"
                className="show-replies-button"
                onClick={() => setVisibleReplies((current) => ({
                  ...current,
                  [comment.id]: (current[comment.id] ?? REPLY_PAGE) + REPLY_PAGE,
                }))}
              >
                Show more replies ({remaining})
              </button>
            ) : (
              <button
                type="button"
                className="show-replies-button hide-replies"
                onClick={() => {
                  setExpanded((current) => ({ ...current, [comment.id]: false }))
                  setVisibleReplies((current) => ({ ...current, [comment.id]: REPLY_PAGE }))
                }}
              >
                Hide replies
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <Modal onClose={onClose} label="Post chat" wide>
      <div className="comments-modal chat-modal">
        <header>
          <button className="icon-button" aria-label="Close chat" onClick={onClose}><X /></button>
          <div>
            <span className="eyebrow">Conversation</span>
            <h2>{app.postType === 'text' || app.postType === 'short' ? 'Post chat' : app.title}</h2>
            <span>{app.comments} message{app.comments === 1 ? '' : 's'}</span>
          </div>
        </header>
        {(app.postBody || app.description) && (
          <div className="chat-original-post">
            <p>{renderRichText(app.postBody || app.description, { onMention: onOpenHandle })}</p>
          </div>
        )}
        <div className="comment-list chat-list">
          {roots.map((comment) => renderMessage(comment))}
          {roots.length === 0 && (
            <EmptyState
              icon={<MessageCircle />}
              title="Start the conversation"
              body="Reply, heart, or repost messages — same actions as the rest of Appy."
            />
          )}
        </div>
        <form onSubmit={submit} className="comment-compose chat-compose">
          <UserAvatar user={currentUser} size="sm" />
          <div className="chat-compose-fields">
            {replyTo && (
              <div className="chat-replying">
                <span>Replying to @{replyTo.handle}</span>
                <button type="button" className="icon-button" aria-label="Cancel reply" onClick={() => setReplyTo(null)}><X size={14} /></button>
              </div>
            )}
            <label className="sr-only" htmlFor="appy-comment">{replyTo ? 'Write a reply' : 'Write a message'}</label>
            <textarea
              id="appy-comment"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder={replyTo ? `Reply to @${replyTo.handle}…` : 'Post a message… use @handle to mention'}
              maxLength={1000}
              rows={2}
            />
            <div className="chat-compose-footer">
              <small>{body.length}/1000</small>
              <button type="submit" className="primary-button small" disabled={!body.trim()}>
                {replyTo ? 'Reply' : 'Send'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  )
}

function Player({ app, onClose, onSave, saved }: { app: AppyCreation; onClose: () => void; onSave: () => void; saved?: boolean }) {
  return <div className="player"><header><button className="player-close" aria-label="Close full screen app" onClick={onClose}><X />Close</button><div><b>{app.title}</b><span>v{app.version} · Appy runtime</span></div><button className={saved ? 'saved' : ''} aria-label={saved ? `Remove ${app.title} from Library` : `Save ${app.title} to Library`} onClick={onSave}><Bookmark fill={saved ? 'currentColor' : 'none'} />{saved ? 'Saved' : 'Save'}</button></header><AppPreview app={app} interactive className="player-frame" /><footer><Shield />Running in Appy’s isolated browser sandbox. Save data is private to this app.</footer></div>
}

function Editor({ app, onClose, onRevise, onPublish, onRestore, onSetCover, onStopBuild }: { app: AppyCreation; onClose: () => void; onRevise: (instruction: string) => Promise<void>; onPublish: (caption: string) => void; onRestore: (version: number) => Promise<number>; onSetCover: (file: File) => Promise<void>; onStopBuild?: () => Promise<void> }) {
  const isMobile = useIsMobileBuilder()
  const [instruction, setInstruction] = useState('')
  const [caption, setCaption] = useState(app.postBody || app.description || '')
  const [working, setWorking] = useState(false)
  const [restoring, setRestoring] = useState<number | null>(null)
  const [stopping, setStopping] = useState(false)
  const [error, setError] = useState('')
  const [viewport, setViewport] = useState<'desktop' | 'tablet' | 'phone'>('desktop')
  const isBuilding = app.status === 'building' || working
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!instruction.trim() || working || app.status === 'building') return
    const pending = instruction.trim()
    setWorking(true)
    setError('')
    try {
      await onRevise(pending)
      setInstruction('')
    } catch (caught) {
      setError(errorText(caught, 'Could not update this app.'))
    } finally {
      setWorking(false)
    }
  }
  const stop = async () => {
    if (!onStopBuild || stopping) return
    setStopping(true)
    setError('')
    try { await onStopBuild() } catch (caught) { setError(errorText(caught, 'Could not stop the build.')) } finally { setStopping(false) }
  }
  const restore = async (version: number) => {
    if (version === app.version || restoring !== null || isBuilding) return
    setRestoring(version)
    setError('')
    try { await onRestore(version) } catch (caught) { setError(errorText(caught, 'Could not restore this version.')) } finally { setRestoring(null) }
  }
  if (isMobile) {
    return (
      <div className="editor-shell editor-shell-mobile-gate">
        <DesktopBuilderNotice onClose={onClose} />
      </div>
    )
  }
  return (
    <div className="editor-shell">
      <header>
        <button className="icon-button" aria-label="Close builder" onClick={onClose}><X /></button>
        <div><span className="eyebrow">Appy Builder · {COMPANY_NAME}</span><b>{app.title}</b></div>
        <div className="editor-header-actions">
          <span className={`status-pill ${app.status}`}>{isBuilding ? 'Building' : `Version ${app.version}`}</span>
          {isBuilding && onStopBuild && (
            <button type="button" className="secondary-button small danger-outline" disabled={stopping} onClick={() => void stop()}>
              {stopping ? 'Stopping…' : 'Stop build'}
            </button>
          )}
          {(app.status === 'ready' || app.status === 'published') && !working && (
            <button className="primary-button small" onClick={() => onPublish(caption)}><Rocket />{app.status === 'published' ? 'Update post' : 'Post with caption'}</button>
          )}
        </div>
      </header>
      <main>
        <aside className="editor-chat">
          <div className="memory-note"><Sparkles /><div><b>Live build log</b><span>Files the model is writing, like a coding session.</span></div></div>
          <div className="chat-history">
            <div className="chat-bubble user-bubble">{app.prompt}</div>
            {(app.buildLog ?? []).map((entry, index) => (
              <div className={`chat-bubble ai-bubble build-log-bubble ${entry.status === 'failed' ? 'build-log-failed' : ''}`} key={`${entry.at}-${index}`}>
                <div className="build-log-meta">
                  {entry.file ? <code className="build-file-chip">{entry.file}</code> : null}
                  <small>{entry.status || 'build'}</small>
                </div>
                {entry.message}
              </div>
            ))}
            {!isBuilding && <div className="chat-bubble ai-bubble">Ask for one specific change and I’ll preserve everything else.</div>}
            {app.versions.slice(1).map((version) => <div className="chat-bubble user-bubble" key={version.number}>{version.note}</div>)}
          </div>
          {(app.status === 'ready' || app.status === 'published') && (
            <label className="caption-field">Post caption<textarea value={caption} onChange={(event) => setCaption(event.target.value)} maxLength={2000} placeholder="Write what this post should say…" /><small>You choose the post text — it is not auto-generated.</small></label>
          )}
          <form onSubmit={submit}>
            <label className="sr-only" htmlFor="appy-change-request">Describe one change</label>
            <textarea id="appy-change-request" value={instruction} onChange={(event) => setInstruction(event.target.value)} placeholder="Change one thing… e.g. Make the player jump higher, but keep the level design." disabled={isBuilding} />
            <button type="submit" className="primary-button small editor-submit" disabled={!instruction.trim() || isBuilding}>{working || app.status === 'building' ? 'Updating…' : 'Update app'}<WandSparkles /></button>
            {error && <p className="form-error" role="alert">{error}</p>}
          </form>
        </aside>
        <section className={`editor-preview viewport-${viewport}`}>
          <div className="viewport-bar">
            {(['desktop', 'tablet', 'phone'] as const).map((size) => (
              <button type="button" key={size} className={viewport === size ? 'selected' : ''} aria-pressed={viewport === size} onClick={() => setViewport(size)}>{size[0].toUpperCase() + size.slice(1)}</button>
            ))}
            <span>Live preview</span>
          </div>
          {isBuilding ? <AppPreview app={app} className="editor-frame" /> : <AppPreview app={app} interactive className="editor-frame" />}
        </section>
        <aside className="editor-details">
          <h3>Details</h3>
          <dl>
            <div><dt>Type</dt><dd>{app.kind}</dd></div>
            <div><dt>Storage</dt><dd>Appy private data</dd></div>
            <div><dt>Network</dt><dd>Blocked</dd></div>
          </dl>
          <h3>App cover image</h3>
          <ImageUploadField label="App cover image" buttonLabel={app.coverUrl ? 'Change cover' : 'Upload cover'} preview={app.coverUrl} previewClassName="cover-thumb" compact onPick={(file) => onSetCover(file).catch((caught) => setError(errorText(caught, 'Could not upload cover.')))} />
          <h3><History />Versions</h3>
          <div className="version-list">
            {[...app.versions].reverse().map((version) => (
              <button key={version.number} className={app.version === version.number ? 'current' : ''} disabled={app.version === version.number || restoring !== null || isBuilding} aria-label={app.version === version.number ? `Version ${version.number}, current` : `Restore version ${version.number}`} onClick={() => void restore(version.number)}>
                <span>v{version.number}</span>
                <div><b>{restoring === version.number ? 'Restoring…' : version.note}</b><small>{relative(version.createdAt)}</small></div>
                {app.version === version.number ? <Check /> : <ChevronRight />}
              </button>
            ))}
          </div>
        </aside>
      </main>
    </div>
  )
}

function Onboarding({ onComplete }: { onComplete: (name: string, handle: string) => Promise<void> }) {
  const [name, setName] = useState('')
  const [handle, setHandle] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting) return
    setSubmitting(true); setError('')
    try { await onComplete(name, handle) } catch (caught) { setError(errorText(caught, 'Could not save your profile.')); setSubmitting(false) }
  }
  return <div className="onboarding"><div className="onboarding-art"><Brand /><h1>Make something.<br />Post something people can use.</h1><p>Apps and games, built from a prompt and shared like a post.</p></div><form onSubmit={submit}><span className="step">1 of 3</span><h2>Create your Appy profile</h2><p>You can change this later.</p><label>Name<input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder="Your name" required /></label><label>Handle<div className="handle-input"><span>@</span><input value={handle} minLength={2} maxLength={30} onChange={(event) => setHandle(event.target.value.replace(/[^a-zA-Z0-9_]/g, ''))} placeholder="handle" required /></div></label><div className="onboarding-points"><span><Sparkles />Generate apps and games</span><span><Layers3 />Keep every version</span><span><Shield />Run safely in Appy</span></div>{error && <p className="form-error" role="alert">{error}</p>}<button className="primary-button" disabled={submitting || !name.trim() || handle.length < 2}>{submitting ? 'Saving profile…' : 'Enter Appy'}<ChevronRight /></button></form></div>
}

function ProfileEditor({ user, onClose, onSave, onUpload }: { user: AppyUser; onClose: () => void; onSave: (profile: { name: string; handle: string; bio: string; avatarUrl?: string | null; bannerUrl?: string | null }) => Promise<void>; onUpload: (kind: 'avatar' | 'banner', file: File) => Promise<string> }) {
  const [name, setName] = useState(user.name)
  const [handle, setHandle] = useState(user.handle)
  const [bio, setBio] = useState(user.bio)
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl ?? null)
  const [bannerUrl, setBannerUrl] = useState(user.bannerUrl ?? null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (saving) return
    setSaving(true); setError('')
    try { await onSave({ name, handle, bio, avatarUrl, bannerUrl }); onClose() } catch (caught) { setError(errorText(caught, 'Could not save your profile.')); setSaving(false) }
  }
  const pick = async (kind: 'avatar' | 'banner', file?: File | null) => {
    if (!file) return
    setError('')
    try {
      const url = await onUpload(kind, file)
      if (kind === 'avatar') setAvatarUrl(url)
      else setBannerUrl(url)
    } catch (caught) { setError(errorText(caught, 'Could not upload image.')) }
  }
  return <Modal onClose={onClose} label="Edit profile"><form className="form-modal profile-edit-modal" onSubmit={submit}><header><div><span className="eyebrow">Your public identity</span><h2>Edit profile</h2></div><button type="button" className="icon-button" aria-label="Close profile editor" onClick={onClose}><X /></button></header><div className="profile-media-fields"><ImageUploadField label="Banner photo" buttonLabel={bannerUrl ? 'Change banner' : 'Upload banner'} preview={bannerUrl} previewClassName="banner-thumb" onPick={(file) => void pick('banner', file)} /><ImageUploadField label="Profile photo" buttonLabel={avatarUrl ? 'Change photo' : 'Upload photo'} preview={avatarUrl} previewClassName="avatar-thumb" compact onPick={(file) => void pick('avatar', file)} /></div><label htmlFor="profile-name">Name<input id="profile-name" autoFocus value={name} maxLength={50} onChange={(event) => setName(event.target.value)} required /></label><label htmlFor="profile-handle">Handle<div className="handle-field"><span>@</span><input id="profile-handle" aria-label="Handle" value={handle} minLength={2} maxLength={30} pattern="[A-Za-z0-9_]+" onChange={(event) => setHandle(event.target.value.replace(/[^a-zA-Z0-9_]/g, ''))} required /></div></label><label htmlFor="profile-bio">Bio<textarea id="profile-bio" aria-label="Bio" value={bio} maxLength={160} onChange={(event) => setBio(event.target.value)} placeholder="What do you make?" /><small>{bio.length}/160</small></label>{error && <p className="form-error" role="alert">{error}</p>}<footer><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button className="primary-button" disabled={saving || !name.trim() || handle.length < 2}>{saving ? 'Saving…' : 'Save profile'}</button></footer></form></Modal>
}

function DailyScheduleEditor({ settings, onClose, onSave }: { settings: ReturnType<typeof useAppyStore>['snapshot']['settings']; onClose: () => void; onSave: (schedule: { enabled: boolean; hour: number; intelligence: Intelligence }) => Promise<void> }) {
  const [enabled, setEnabled] = useState(settings.dailyGame)
  const [hour, setHour] = useState(settings.dailyGameHour)
  const [intelligence, setIntelligence] = useState(settings.dailyGameIntelligence)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setSaving(true); setError('')
    try { await onSave({ enabled, hour, intelligence }); onClose() } catch (caught) { setError(errorText(caught, 'Could not save the Daily Game schedule.')); setSaving(false) }
  }
  return <Modal onClose={onClose} label="Daily Game schedule"><form className="form-modal schedule-modal" onSubmit={submit}><header><div><span className="eyebrow">Automatic creation</span><h2>Daily Game schedule</h2></div><button type="button" className="icon-button" aria-label="Close Daily Game schedule" onClick={onClose}><X /></button></header><div className="modal-setting"><div><b>Generate and post daily</b><span>This spends build credits only when a game is generated.</span></div><button type="button" className={`switch ${enabled ? 'on' : ''}`} role="switch" aria-label="Enable Daily Game schedule" aria-checked={enabled} onClick={() => setEnabled(!enabled)}><i /></button></div><label htmlFor="daily-hour">Run time<select id="daily-hour" aria-label="Run time" value={hour} onChange={(event) => setHour(Number(event.target.value))}>{Array.from({ length: 24 }, (_, value) => <option key={value} value={value}>{formatHour(value)}</option>)}</select><small>Uses your current time zone: {Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'}.</small></label><fieldset><legend>Build intelligence</legend><div className="quality-buttons">{(['low', 'medium', 'high'] as Intelligence[]).map((level) => <button type="button" key={level} className={intelligence === level ? 'selected' : ''} aria-pressed={intelligence === level} onClick={() => setIntelligence(level)}>{level}</button>)}</div></fieldset><div className="schedule-note"><Rocket /><p><b>Auto-post is on.</b><span>Each finished Daily Game is published automatically. Disable the schedule any time to stop future builds.</span></p></div>{error && <p className="form-error" role="alert">{error}</p>}<footer><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button className="primary-button" disabled={saving}>{saving ? 'Saving…' : 'Save schedule'}</button></footer></form></Modal>
}

function SafetyModal({ store, onClose }: { store: ReturnType<typeof useAppyStore>; onClose: () => void }) {
  const { settings } = store.snapshot
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const visibility = async (value: typeof settings.profileVisibility) => {
    setSaving(true); setError('')
    try { await store.setProfileVisibility(value) } catch (caught) { setError(errorText(caught, 'Could not update profile visibility.')) } finally { setSaving(false) }
  }
  return <Modal onClose={onClose} label="Privacy and safety"><div className="form-modal safety-modal"><header><div><span className="eyebrow">You control what leaves Appy</span><h2>Privacy and safety</h2></div><button className="icon-button" aria-label="Close privacy and safety" onClick={onClose}><X /></button></header><section><h3>Profile visibility</h3><p>Private profiles are hidden from generated apps that request your public Appy profile.</p><div className="choice-cards"><button disabled={saving} className={settings.profileVisibility === 'public' ? 'selected' : ''} aria-pressed={settings.profileVisibility === 'public'} onClick={() => void visibility('public')}><Eye /><b>Public</b><span>Creators and apps can see your handle, bio, and public profile.</span></button><button disabled={saving} className={settings.profileVisibility === 'private' ? 'selected' : ''} aria-pressed={settings.profileVisibility === 'private'} onClick={() => void visibility('private')}><Shield /><b>Private</b><span>Your public profile API returns no personal profile data.</span></button></div></section><section><h3>Safer previews</h3><div className="modal-setting"><div><b>Autoplay app previews</b><span>Off keeps every interactive preview paused until you open it.</span></div><button className={`switch ${settings.autoplayPreviews ? 'on' : ''}`} role="switch" aria-label="Autoplay app previews" aria-checked={settings.autoplayPreviews} onClick={() => store.setSetting('autoplayPreviews', !settings.autoplayPreviews)}><i /></button></div><div className="modal-setting"><div><b>Browser notifications</b><span>Control Appy alerts on this device.</span></div><button className={`switch ${settings.pushNotifications ? 'on' : ''}`} role="switch" aria-label="Browser notifications in privacy" aria-checked={settings.pushNotifications} onClick={() => store.setSetting('pushNotifications', !settings.pushNotifications)}><i /></button></div></section>{error && <p className="form-error" role="alert">{error}</p>}<footer><span>Generated apps run without same-origin or network access.</span><button className="primary-button" onClick={onClose}>Done</button></footer></div></Modal>
}

function AccountModal({ store, onClose, initialMode = 'signup' }: { store: ReturnType<typeof useAppyStore>; onClose: () => void; initialMode?: 'signup' | 'signin' }) {
  const account = store.snapshot.account
  const [mode, setMode] = useState<'signup' | 'signin'>(account.status === 'secured' || account.status === 'verification-pending' ? 'signin' : initialMode)
  const [email, setEmail] = useState(account.email ?? '')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [needsMfa, setNeedsMfa] = useState(false)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const finishSignedIn = (withMfa: boolean) => {
    setMessage(withMfa
      ? 'Signed in with two-step authentication. Staff tools are unlocked.'
      : 'Signed in. Your apps, credits, and billing are restored.')
    setPassword('')
    setConfirm('')
    setMfaCode('')
    setNeedsMfa(false)
    window.setTimeout(onClose, 700)
  }
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError(''); setMessage('')
    if (needsMfa) {
      if (!mfaCode.trim()) { setError('Enter the 6-digit code from your authenticator app.'); return }
      setBusy(true)
      try {
        await store.verifyMfaLogin(mfaCode)
        finishSignedIn(true)
      } catch (caught) {
        setError(errorText(caught, 'That authenticator code was not accepted.'))
      } finally {
        setBusy(false)
      }
      return
    }
    if (mode === 'signup' && password !== confirm) { setError('Passwords do not match.'); return }
    if (password.length < 8) { setError('Use at least 8 characters for your password.'); return }
    setBusy(true)
    try {
      if (mode === 'signup') {
        const result = await store.signUp(email, password)
        setMessage(result.verificationRequired
          ? `Account created for ${result.email}. Check your inbox if a confirmation email was sent, then sign in.`
          : `Account ready — signed in as ${result.email}.`)
        if (!result.verificationRequired) window.setTimeout(onClose, 900)
      } else {
        const result = await store.signIn(email, password)
        if (result.mfaRequired) {
          setNeedsMfa(true)
          setPassword('')
          setMessage('Enter the 6-digit code from your authenticator app to finish signing in.')
        } else {
          finishSignedIn(false)
        }
      }
      if (mode === 'signup') { setPassword(''); setConfirm('') }
    } catch (caught) {
      setError(errorText(caught, mode === 'signup' ? 'Could not create this account.' : 'Could not sign in.'))
    } finally {
      setBusy(false)
    }
  }
  if (store.mode === 'demo') {
    return <Modal onClose={onClose} label="Account"><div className="form-modal account-modal"><header><div><span className="eyebrow">Local demo</span><h2>Your data is browser-only</h2></div><button className="icon-button" aria-label="Close account" onClick={onClose}><X /></button></header><div className="account-explainer"><Shield /><p><b>No online account is active.</b><span>This demo saves to local browser storage. Create account, sign-in, and Stripe checkout become available when Appy is connected to Supabase.</span></p></div><footer><button className="primary-button" onClick={onClose}>Got it</button></footer></div></Modal>
  }
  const secured = account.status === 'secured' && !needsMfa
  const pending = account.status === 'verification-pending' && !needsMfa
  return <Modal onClose={onClose} label="Account"><form className="form-modal account-modal" onSubmit={submit}><header><div><span className="eyebrow">Your Appy account</span><h2>{needsMfa ? 'Authenticator code' : secured ? 'Signed in' : pending ? 'Verify your email' : mode === 'signup' ? 'Create account' : 'Sign in'}</h2></div><button type="button" className="icon-button" aria-label="Close account" onClick={onClose}><X /></button></header>{needsMfa ? <><div className="account-explainer"><Shield /><p><b>Two-step authentication</b><span>This account has an authenticator app. Enter the current 6-digit code to finish signing in and unlock staff tools.</span></p></div><label>Authenticator code<input type="text" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9 ]{6,8}" maxLength={8} value={mfaCode} onChange={(event) => setMfaCode(event.target.value)} required autoFocus /></label></> : secured ? <div className="account-explainer"><Check /><p><b>{account.email}</b><span>Your apps, library, credits, and subscription can be restored on another device. Verify this email before checkout if you have not already.</span></p></div> : pending ? <div className="account-explainer"><Shield /><p><b>{account.email}</b><span>Open the confirmation link we sent, then return here to buy plans or credits. You can keep building while you wait.</span></p></div> : <><div className="modal-tabs"><button type="button" className={mode === 'signup' ? 'selected' : ''} aria-pressed={mode === 'signup'} onClick={() => { setMode('signup'); setError(''); setMessage(''); setNeedsMfa(false) }}>Create account</button><button type="button" className={mode === 'signin' ? 'selected' : ''} aria-pressed={mode === 'signin'} onClick={() => { setMode('signin'); setError(''); setMessage(''); setNeedsMfa(false) }}>Sign in</button></div><p className="modal-intro">{mode === 'signup' ? 'Create an email and password so you can keep your apps, buy credits, and sign in on other devices.' : 'Sign in to restore apps, credits, and billing from an existing Appy account.'}</p><label>Email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label><label>Password<input type="password" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} value={password} onChange={(event) => setPassword(event.target.value)} minLength={8} required /></label>{mode === 'signup' && <label>Confirm password<input type="password" autoComplete="new-password" value={confirm} onChange={(event) => setConfirm(event.target.value)} minLength={8} required /></label>}</>}{message && <p className="form-success" role="status">{message}</p>}{error && <p className="form-error" role="alert">{error}</p>}<footer>{(secured || pending) && <button type="button" className="secondary-button" disabled={busy} onClick={() => { setBusy(true); void store.signOut().then(onClose).catch((caught) => { setError(errorText(caught, 'Could not sign out.')); setBusy(false) }) }}>Sign out</button>}<button type="button" className="secondary-button" onClick={onClose}>Close</button>{needsMfa ? <button className="primary-button" disabled={busy}>{busy ? 'Verifying…' : 'Verify code'}</button> : !secured && !pending && <button className="primary-button" disabled={busy}>{busy ? 'Working…' : mode === 'signup' ? 'Create account' : 'Sign in'}</button>}</footer></form></Modal>
}

type InfoKind = 'terms' | 'privacy' | 'help'
function InfoModal({ kind, onClose, onBuild }: { kind: InfoKind; onClose: () => void; onBuild: () => void }) {
  const content = {
    terms: {
      title: 'Terms of Service',
      eyebrow: 'Effective July 19, 2026',
      sections: [
        ['1. Agreement', 'By creating an Appy account, browsing as a guest, publishing content, buying credits, or using any Appy feature, you agree to these Terms of Service. If you do not agree, do not use Appy. Appy is a product of Starex. Starex may update these terms; continued use after an update means you accept the revised terms.'],
        ['2. Eligibility and accounts', 'You must be able to form a binding contract in your jurisdiction. You are responsible for your login credentials, authenticator devices, and activity under your account. Do not share accounts or impersonate others. We may suspend or terminate accounts that violate these terms, including bans applied by staff.'],
        ['3. The Appy service', 'Appy lets you generate apps and games from prompts, publish them socially, post text and images, follow creators, comment, like, repost, and use build credits. Features may change, be rate-limited, or be unavailable during maintenance. Demo or local modes may store data only in your browser and are not a substitute for a secured account.'],
        ['4. Acceptable use', 'You may not use Appy to break the law; harass, threaten, or dox people; distribute malware; spam; scrape or abuse the service; bypass billing, moderation, or safety systems; post sexual content involving minors; or upload content you do not have rights to share. Staff may remove content, ban accounts, and act on reports without prior notice when needed to protect users or the service.'],
        ['5. Content you post', 'You retain rights you already have in your prompts, captions, images, and other materials. You grant Appy a worldwide, non-exclusive license to host, display, moderate, and distribute your public content as needed to operate the product (feeds, profiles, notifications, backups, and safety review). You are responsible for reviewing AI-generated apps before publishing and for any claims arising from your content.'],
        ['6. AI generation', 'Builds are produced with third-party AI models. Output may be inaccurate, incomplete, or unsafe. Appy does not guarantee fitness for any purpose. Generation consumes build credits based on measured model usage. Unlimited credits, when granted, are an account entitlement and may be revoked for abuse.'],
        ['7. Credits, subscriptions, and payments', 'Paid plans and top-ups are processed by Stripe. Subscriptions renew until cancelled through the Stripe customer portal (Manage subscription). Monthly promotional credit allowances may expire at renewal. Platform fees (such as top-up fees) are disclosed at checkout. Refunds follow Stripe and applicable law; disputed charges may pause generation until resolved.'],
        ['8. Intellectual property', 'Appy branding, software, design, and trademarks remain the property of Starex. You may not copy, reverse engineer, or resell the service except as allowed by law.'],
        ['9. Disclaimers', 'Appy is provided “as is” without warranties of merchantability, fitness for a particular purpose, or non-infringement to the fullest extent permitted by law. We do not warrant uninterrupted or error-free operation.'],
        ['10. Limitation of liability', 'To the fullest extent permitted by law, Starex and its operators are not liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits, data, or goodwill, arising from your use of the service. Our aggregate liability for claims relating to Appy is limited to the greater of (a) amounts you paid us for Appy in the 3 months before the claim or (b) USD $50.'],
        ['11. Termination', 'You may stop using Appy at any time. We may suspend or terminate access for violations, risk, or operational reasons. Provisions that should survive (including licenses needed to keep public posts available, disclaimers, and liability limits) continue after termination.'],
        ['12. Contact', 'Questions about these Terms: contact Starex via the account email associated with your project or through in-app Help. For billing, use Manage subscription / Stripe support channels for payment method issues.'],
      ],
    },
    privacy: {
      title: 'Privacy Policy',
      eyebrow: 'Effective July 19, 2026',
      sections: [
        ['1. Overview', 'This Privacy Policy explains what Appy (a product of Starex) collects, how it is used, and the choices you have. Appy is a social app builder: we process account, social, billing, and generation data so the product works.'],
        ['2. Information we collect', 'Account data: email, password hashes (via Supabase Auth), display name, handle, bio, avatar, banner, verification and staff roles. Social data: posts, captions, images, comments, likes, reposts, follows, reports, and notifications. Usage data: plays, impressions, dwell time on posts, device/browser signals needed for security, and diagnostic logs. Billing data: plan status, credit balance and ledger, Stripe customer and subscription identifiers (card details are handled by Stripe, not stored on Appy servers). Generation data: prompts, revision instructions, model job metadata, and produced app source for builds you request.'],
        ['3. How we use information', 'We use data to provide and improve Appy; personalize feeds (for example follows, likes, comments, and view time); run AI builds; process payments and credits; send transactional notifications; moderate content (including automated image review); prevent fraud and abuse; and comply with law.'],
        ['4. AI and third-party processors', 'Prompts and related build context are sent to our configured AI provider to generate or revise apps. Images may be reviewed by AI for prohibited sexual content before publishing. Payments are processed by Stripe. Authentication, database, and storage run on Supabase. These providers process data under their own terms and safeguards.'],
        ['5. Sharing', 'We do not sell your personal information. We share data with service providers who power Auth, storage, payments, and AI; when you publish publicly (other users can see your public profile and posts); when required by law or to protect rights and safety; or with your direction (for example opening the Stripe portal).'],
        ['6. Public vs private', 'Public profiles and public posts are visible to other users and may appear in feeds, search, and trending. You can set profile visibility to private in Settings → Privacy and safety so generated apps cannot read your public profile via the runtime API. Private drafts and unpublished builds remain limited to your account subject to normal infrastructure access.'],
        ['7. Retention', 'We keep account and content data while your account is active and as needed for backups, dispute resolution, legal obligations, and security. You may delete posts you own. Staff may remove violating content. Anonymous guest sessions should not create lasting public profiles.'],
        ['8. Security', 'We use industry-standard measures including encrypted transport, access controls, server-side enforcement of credits and roles, and optional TOTP two-step authentication. No method of transmission or storage is 100% secure.'],
        ['9. Your choices', 'Update profile fields in Edit profile; manage privacy in Privacy and safety; manage billing in Build credits; reset passwords and enable 2FA in Settings; report content via the post menu; contact staff for ban appeals when applicable. You may request access or deletion of personal data where required by applicable law by contacting Starex.'],
        ['10. Children', 'Appy is not directed to children under 13 (or the minimum age required in your region). Do not use Appy if you are under that age.'],
        ['11. International processing', 'Data may be processed in the United States and other regions where our providers operate. By using Appy you understand that transfers may occur subject to provider safeguards.'],
        ['12. Changes', 'We may update this Privacy Policy. Material changes will be reflected by the effective date above and, when appropriate, an in-app notice. Continued use after changes means you accept the updated policy.'],
        ['13. Contact', 'Privacy questions: reach Starex through your secured account email channel or in-app Help. For payment-card data requests, contact Stripe as the payment processor.'],
      ],
    },
    help: {
      title: 'Help center',
      eyebrow: 'The essentials',
      sections: [
        ['Build an app', 'On a computer, choose Post → Build, describe the complete app or game, select intelligence, and leave the build running. App building is not available on phones — open https://appy-6kra.onrender.com on a computer. Appy is a product of Starex.'],
        ['Edit precisely', 'Open one of your builds and describe one specific change. Appy keeps the original prompt and version history as context.'],
        ['Credits and billing', 'Build credits track measured AI usage. Open Build credits to add more, choose a plan, or manage an active subscription in Stripe.'],
        ['Legal', 'Open Terms of Service and Privacy Policy anytime from Settings → Legal, or from the footer links on the home layout.'],
      ],
    },
  }[kind]
  return (
    <Modal onClose={onClose} label={content.title} wide={kind === 'terms' || kind === 'privacy'}>
      <div className={`info-modal ${(kind === 'terms' || kind === 'privacy') ? 'legal-modal' : ''}`}>
        <header>
          <div>
            <span className="eyebrow">{content.eyebrow}</span>
            <h2>{content.title}</h2>
          </div>
          <button className="icon-button" aria-label={`Close ${content.title}`} onClick={onClose}><X /></button>
        </header>
        <div className="legal-scroll">
          {content.sections.map(([title, body]) => (
            <section key={title}>
              <h3>{title}</h3>
              <p>{body}</p>
            </section>
          ))}
        </div>
        <footer>
          {kind === 'help' && <button className="secondary-button" onClick={() => { onClose(); onBuild() }}>Start a build</button>}
          <button className="primary-button" onClick={onClose}>Close</button>
        </footer>
      </div>
    </Modal>
  )
}

function SettingsView({ store, onToast, onOpenLegal }: { store: ReturnType<typeof useAppyStore>; onToast: (value: string) => void; onOpenLegal: (kind: 'terms' | 'privacy') => void }) {
  const { settings, account } = store.snapshot
  const [dailyEditor, setDailyEditor] = useState(false)
  const [safety, setSafety] = useState(false)
  const [accountOpen, setAccountOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [resetEmail, setResetEmail] = useState(account.email ?? '')
  const [mfaSetup, setMfaSetup] = useState<{ id: string; qrCode: string; secret: string } | null>(null)
  const [mfaCode, setMfaCode] = useState('')
  const [securityBusy, setSecurityBusy] = useState(false)
  const toggleDaily = async () => {
    try { await store.configureDailyGame({ enabled: !settings.dailyGame, hour: settings.dailyGameHour, intelligence: settings.dailyGameIntelligence }); onToast(settings.dailyGame ? 'Daily Game paused' : 'Daily Game enabled') } catch (error) { onToast(errorText(error, 'Could not update Daily Game')) }
  }
  const accountLabel = account.status === 'secured'
    ? 'Account'
    : account.status === 'verification-pending'
      ? 'Verify your email'
      : 'Create account or sign in'
  const accountDetail = store.mode === 'demo'
    ? 'This demo is saved only in this browser.'
    : account.status === 'secured'
      ? account.email
      : account.status === 'verification-pending'
        ? `Confirm ${account.email} to unlock Stripe checkout.`
        : 'Email sign-in keeps your apps, credits, and subscriptions.'
  const runSecurity = async (action: () => Promise<void>, success: string) => {
    setSecurityBusy(true)
    try { await action(); onToast(success) } catch (error) { onToast(errorText(error, 'Security update failed.')) } finally { setSecurityBusy(false) }
  }
  return <div className="page-view"><header className="page-header"><h1>Settings</h1><p>Generation, notifications, privacy, and appearance.</p></header><section className="settings-section"><h2>Generation</h2><div className="setting-row"><div><b>Default intelligence</b><span>Medium balances quality, speed, and credit use.</span></div><div className="quality-buttons">{(['low', 'medium', 'high'] as Intelligence[]).map((level) => <button className={settings.intelligence === level ? 'selected' : ''} aria-pressed={settings.intelligence === level} key={level} onClick={() => { store.setIntelligence(level); onToast(`Default intelligence set to ${level}`) }}>{level}</button>)}</div></div><div className="setting-row"><div><b>Daily Game</b><span>Uses credits and automatically posts one generated game each day. Off by default.</span></div><button className={`switch ${settings.dailyGame ? 'on' : ''}`} role="switch" aria-label="Daily Game" aria-checked={settings.dailyGame} onClick={() => void toggleDaily()}><i /></button></div>{settings.dailyGame && <div className="daily-config"><Clock3 /><div><b>Next run tomorrow at {formatHour(settings.dailyGameHour)}</b><span>Games · {settings.dailyGameIntelligence[0].toUpperCase() + settings.dailyGameIntelligence.slice(1)} · Auto-post · Charged by usage</span></div><button onClick={() => setDailyEditor(true)}>Edit</button></div>}</section><section className="settings-section"><h2>Experience</h2>{[['autoplayPreviews', 'Autoplay previews', 'Start lightweight previews when they enter the feed.'], ['emailBuilds', 'Build completion email', 'Email you when a build finishes.'], ['pushNotifications', 'Browser notifications', 'Show build and social alerts.'], ['reduceMotion', 'Reduce motion', 'Limit animation throughout Appy.']].map(([key, title, body]) => <div className="setting-row" key={key}><div><b>{title}</b><span>{body}</span></div><button className={`switch ${settings[key as keyof typeof settings] ? 'on' : ''}`} role="switch" aria-label={title} aria-checked={Boolean(settings[key as keyof typeof settings])} onClick={() => store.setSetting(key as 'autoplayPreviews', !settings[key as 'autoplayPreviews'])}><i /></button></div>)}</section><section className="settings-section"><h2>Appearance</h2><div className="theme-grid"><button className={settings.theme === 'dark' ? 'selected' : ''} aria-pressed={settings.theme === 'dark'} onClick={() => store.setSetting('theme', 'dark')}><Moon />Dark</button><button className={settings.theme === 'light' ? 'selected' : ''} aria-pressed={settings.theme === 'light'} onClick={() => store.setSetting('theme', 'light')}><Sun />Light</button><button className={settings.theme === 'system' ? 'selected' : ''} aria-pressed={settings.theme === 'system'} onClick={() => store.setSetting('theme', 'system')}><Settings />System</button></div></section><section className="settings-section"><h2>Account and safety</h2><button className="settings-link" aria-label={accountLabel} onClick={() => setAccountOpen(true)}><User /><div><b>{accountLabel}</b><span>{accountDetail}</span></div><ChevronRight /></button><button className="settings-link" aria-label="Privacy and safety" onClick={() => setSafety(true)}><Shield /><div><b>Privacy and safety</b><span>{settings.profileVisibility === 'private' ? 'Private profile' : 'Public profile'} · app permissions and device alerts.</span></div><ChevronRight /></button>{store.mode === 'demo' && <button className="settings-link" onClick={() => { store.resetDemo(); onToast('Demo data reset') }}><LogOut /><div><b>Reset local demo</b><span>Restore Appy’s original sample account and feed.</span></div><ChevronRight /></button>}</section><section className="settings-section"><h2>Legal</h2><button type="button" className="settings-link" aria-label="Terms of Service" onClick={() => onOpenLegal('terms')}><Shield /><div><b>Terms of Service</b><span>Rules for using Appy, content, credits, and accounts.</span></div><ChevronRight /></button><button type="button" className="settings-link" aria-label="Privacy Policy" onClick={() => onOpenLegal('privacy')}><Eye /><div><b>Privacy Policy</b><span>What we collect, how we use it, and your choices.</span></div><ChevronRight /></button></section>{store.mode === 'supabase' && account.status === 'secured' && <section className="settings-section"><h2>Password and two-step authentication</h2><div className="setting-row security-stack"><div><b>Reset password by email</b><span>Supabase sends a secure reset link to your inbox.</span></div><div className="security-inline"><input type="email" value={resetEmail} onChange={(event) => setResetEmail(event.target.value)} placeholder="Email" /><button className="secondary-button" disabled={securityBusy || !resetEmail.trim()} onClick={() => void runSecurity(() => store.resetPassword(resetEmail), 'Password reset email sent')}>Send reset link</button></div></div><div className="setting-row security-stack"><div><b>Set a new password</b><span>Use after opening a reset link, or while signed in.</span></div><div className="security-inline"><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="New password" minLength={8} /><button className="secondary-button" disabled={securityBusy || password.length < 8} onClick={() => void runSecurity(async () => { await store.updatePassword(password); setPassword('') }, 'Password updated')}>Update password</button></div></div><div className="setting-row security-stack"><div><b>Two-step authentication (TOTP)</b><span>{account.mfaEnabled ? 'Enabled with an authenticator app.' : 'Optional extra protection via Supabase MFA.'}</span></div>{account.mfaEnabled ? <button className="secondary-button" disabled={securityBusy} onClick={() => void runSecurity(async () => { const factors = await store.listMfaFactors(); const active = factors.find((factor) => factor.status === 'verified'); if (active) await store.unenrollMfa(active.id) }, 'Two-step authentication turned off')}>Turn off 2FA</button> : !mfaSetup ? <button className="secondary-button" disabled={securityBusy} onClick={() => void runSecurity(async () => { const enrolled = await store.enrollMfa(); setMfaSetup({ id: enrolled.id, qrCode: enrolled.qrCode, secret: enrolled.secret }) }, 'Scan the QR code with your authenticator app')}>Enable 2FA</button> : <div className="mfa-setup">{mfaSetup.qrCode.startsWith('data:') ? <img src={mfaSetup.qrCode} alt="MFA QR code" className="mfa-qr" /> : <p className="modal-intro">Secret: {mfaSetup.secret}</p>}<div className="security-inline"><input value={mfaCode} onChange={(event) => setMfaCode(event.target.value)} placeholder="6-digit code" inputMode="numeric" /><button className="primary-button" disabled={securityBusy || mfaCode.trim().length < 6} onClick={() => void runSecurity(async () => { await store.verifyMfaEnrollment(mfaSetup.id, mfaCode); setMfaSetup(null); setMfaCode('') }, 'Two-step authentication enabled')}>Confirm</button></div></div>}</div></section>}{dailyEditor && <DailyScheduleEditor settings={settings} onClose={() => setDailyEditor(false)} onSave={async (schedule) => { await store.configureDailyGame(schedule); onToast(schedule.enabled ? 'Daily Game schedule saved' : 'Daily Game is off') }} />}{safety && <SafetyModal store={store} onClose={() => setSafety(false)} />}{accountOpen && <AccountModal store={store} onClose={() => setAccountOpen(false)} />}</div>
}

function BillingView({ store, onToast, onOpenAccount }: { store: ReturnType<typeof useAppyStore>; onToast: (value: string) => void; onOpenAccount: () => void }) {
  const [topupInput, setTopupInput] = useState('5')
  const [busy, setBusy] = useState<string | null>(null)
  const topup = Math.min(500, Math.max(5, Number(topupInput) || 5))
  const { billing, account } = store.snapshot
  const needsAccount = store.mode !== 'demo' && account.status !== 'secured'
  const run = async (key: string, action: () => Promise<unknown>) => {
    if (busy) return
    if (needsAccount) {
      onToast(account.status === 'verification-pending'
        ? 'Confirm your email, then return to open live Stripe checkout.'
        : 'Create an account and verify your email before checkout.')
      onOpenAccount()
      return
    }
    setBusy(key)
    try { await action() } catch (error) { onToast(errorText(error, 'Billing is temporarily unavailable.')) } finally { setBusy(null) }
  }
  const creator = featuresForPlan('creator')
  const pro = featuresForPlan('studio')
  const free = featuresForPlan('starter')
  return <div className="page-view billing-view"><header className="page-header"><h1>Build credits &amp; plans</h1><p>{billing.unlimited ? 'This account has unlimited server-side build credits.' : 'Credits power AI builds. Plans unlock longer posts, larger media, and better Shorts.'}</p></header>{needsAccount && <div className="billing-account-banner" role="status"><Shield /><div><b>{account.status === 'verification-pending' ? 'Verify your email to unlock checkout' : 'Create an account to buy credits'}</b><span>Stripe Checkout is live. Appy only opens it for verified email accounts so purchases can be restored later.</span></div><button className="primary-button small" onClick={onOpenAccount}>{account.status === 'verification-pending' ? 'Account status' : 'Create account'}</button></div>}<section className="credit-hero"><div><span>Available balance</span><strong>{formatCredits(billing)}</strong><small>{billing.unlimited ? 'Unlimited · enforced by Supabase' : `$${billing.usageThisMonth.toFixed(2)} used this month`}</small></div><div className="credit-ring" style={{ '--progress': `${billing.unlimited ? 100 : Math.min(100, billing.creditBalance / Math.max(1, billing.creditAllowance) * 100)}%` } as React.CSSProperties}><span>{billing.unlimited ? '∞' : `${Math.round(billing.creditBalance / Math.max(1, billing.creditAllowance) * 100)}%`}</span></div></section>{billing.plan !== 'starter' && <div className="manage-billing"><div><b>{billing.plan === 'studio' ? 'Pro' : 'Creator'} subscription</b><span>Renews {new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(new Date(billing.renewsAt))}</span></div><button disabled={Boolean(busy)} onClick={() => void run('portal', store.openBillingPortal)}>{busy === 'portal' ? 'Opening…' : 'Manage subscription'}<ChevronRight /></button></div>}<h2 className="section-title">Plans</h2><div className="plan-grid plan-grid-3"><article><span>Free</span><h3>{free.priceLabel}</h3><b>{free.monthlyCreditsLabel}</b><p>Post, Shorts, and build with pay-as-you-go credits.</p><ul>{free.features.map((item) => <li key={item}><Check />{item}</li>)}</ul><button disabled className={billing.plan === 'starter' ? 'current-plan' : ''}>{billing.plan === 'starter' ? 'Current plan' : 'Free forever'}</button></article><article><span>Creator</span><h3>$24<small>/month</small></h3><b>{creator.monthlyCreditsLabel}</b><p>Longer posts, HD media, and creator tools.</p><ul>{creator.features.map((item) => <li key={item}><Check />{item}</li>)}</ul><button disabled={Boolean(busy) || billing.plan === 'creator'} className={billing.plan === 'creator' ? 'current-plan' : ''} onClick={() => void run('creator', () => store.purchasePlan('creator'))}>{busy === 'creator' ? 'Opening…' : billing.plan === 'creator' ? 'Current plan' : 'Choose Creator'}</button></article><article className="featured-plan"><span>Pro</span><h3>$34<small>/month</small></h3><b>{pro.monthlyCreditsLabel}</b><p>Up to 1,000,000 words, ultra media, and priority builds.</p><ul>{pro.features.map((item) => <li key={item}><Check />{item}</li>)}</ul><button disabled={Boolean(busy) || billing.plan === 'studio'} className={billing.plan === 'studio' ? 'current-plan' : ''} onClick={() => void run('pro', () => store.purchasePlan('pro'))}>{busy === 'pro' ? 'Opening…' : billing.plan === 'studio' ? 'Current plan' : 'Choose Pro'}</button></article></div><section className="topup-card"><div><CreditCard /><div><h2>Add build credits</h2><p>Pick a credit amount. A $2 platform fee is added at checkout.</p></div></div><label htmlFor="credit-amount">Credit amount</label><div className="credit-amount-input"><span>$</span><input id="credit-amount" type="number" min="5" max="500" inputMode="decimal" value={topupInput} onChange={(event) => setTopupInput(event.target.value)} onBlur={() => setTopupInput(String(topup))} /></div><dl><div><dt>Build credits</dt><dd>${topup.toFixed(2)}</dd></div><div><dt>Platform fee</dt><dd>$2.00</dd></div><div><dt>Total before tax</dt><dd>${(topup + 2).toFixed(2)}</dd></div></dl><button className="primary-button" disabled={Boolean(busy)} onClick={() => { setTopupInput(String(topup)); void run('topup', () => store.purchaseCredits(topup)) }}>{busy === 'topup' ? 'Opening checkout…' : 'Continue to secure checkout'}<ChevronRight /></button><small>Live Stripe Checkout is created server-side. Credits appear only after a verified webhook — never from a local fake balance update.</small></section></div>
}

function AdminView({ store, onToast, onOpenProfile, onOpenSettings }: {
  store: ReturnType<typeof useAppyStore>
  onToast: (text: string) => void
  onOpenProfile: (userId: string) => void
  onOpenSettings?: () => void
}) {
  type AdminTab = 'overview' | 'reports' | 'banned' | 'posts'
  const [tab, setTab] = useState<AdminTab>('overview')
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [reports, setReports] = useState<AdminReport[]>([])
  const [reportFilter, setReportFilter] = useState<string>('open')
  const [banned, setBanned] = useState<AdminBannedUser[]>([])
  const [posts, setPosts] = useState<AdminPostRow[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [mfaCode, setMfaCode] = useState('')
  const [mfaBusy, setMfaBusy] = useState(false)

  const load = useCallback(async (nextTab: AdminTab = tab) => {
    setLoading(true)
    setError('')
    try {
      if (nextTab === 'overview') setStats(await store.staffAdminStats())
      if (nextTab === 'reports') setReports(await store.staffListReports(reportFilter === 'all' ? null : reportFilter))
      if (nextTab === 'banned') setBanned(await store.staffListBannedUsers())
      if (nextTab === 'posts') {
        setPosts(await store.staffListRecentPosts(80))
        setSelected(new Set())
      }
    } catch (caught) {
      setError(errorText(caught, 'Could not load admin data.'))
    } finally {
      setLoading(false)
    }
  }, [reportFilter, store, tab])

  useEffect(() => { void load(tab) }, [load, tab])
  useEffect(() => {
    if (tab === 'reports') void load('reports')
  }, [reportFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const needsMfa = /two-step|2fa|authenticator|aal2/i.test(error)

  const unlockWithMfa = async (event: FormEvent) => {
    event.preventDefault()
    setMfaBusy(true)
    setError('')
    try {
      await store.verifyMfaLogin(mfaCode)
      setMfaCode('')
      onToast('Two-step authentication verified — admin tools unlocked.')
      await load(tab)
    } catch (caught) {
      setError(errorText(caught, 'That authenticator code was not accepted.'))
    } finally {
      setMfaBusy(false)
    }
  }

  const run = async (action: () => Promise<void>, success: string) => {
    setBusy(true)
    try {
      await action()
      onToast(success)
      await load(tab)
    } catch (caught) {
      const message = errorText(caught, 'Admin action failed.')
      onToast(message)
      setError(message)
    } finally {
      setBusy(false)
    }
  }

  const togglePost = (id: string) => {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="page-view admin-page">
      <header className="page-header">
        <h1>Admin</h1>
        <p>Ban list, report queue, and mass moderation tools for Appy staff.</p>
      </header>
      <div className="admin-tabs">
        {([
          ['overview', 'Overview'],
          ['reports', 'Reports'],
          ['banned', 'Ban list'],
          ['posts', 'Mass delete'],
        ] as const).map(([id, label]) => (
          <button key={id} type="button" className={tab === id ? 'selected' : ''} aria-pressed={tab === id} onClick={() => setTab(id)}>
            {label}
            {id === 'reports' && stats && stats.open_reports > 0 ? <b>{stats.open_reports}</b> : null}
          </button>
        ))}
      </div>
      {error && !needsMfa && <p className="form-error" role="alert">{error}</p>}
      {needsMfa && (
        <form className="admin-mfa-panel" onSubmit={(event) => { void unlockWithMfa(event) }}>
          <div className="account-explainer">
            <Shield />
            <p>
              <b>Unlock admin with your authenticator</b>
              <span>Staff tools need a fresh two-step code. Enter the 6-digit code from your authenticator app (you already have 2FA enabled on this account).</span>
            </p>
          </div>
          <label>Authenticator code
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9 ]{6,8}"
              maxLength={8}
              value={mfaCode}
              onChange={(event) => setMfaCode(event.target.value)}
              required
              autoFocus
            />
          </label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="admin-mfa-actions">
            <button type="submit" className="primary-button" disabled={mfaBusy || !mfaCode.trim()}>
              {mfaBusy ? 'Verifying…' : 'Unlock Admin'}
            </button>
            {onOpenSettings && (
              <button type="button" className="secondary-button" onClick={onOpenSettings}>Open Settings</button>
            )}
          </div>
        </form>
      )}
      {loading ? <p className="admin-loading">Loading admin data…</p> : null}

      {tab === 'overview' && stats && (
        <div className="admin-stats">
          <article><span>Open reports</span><strong>{stats.open_reports}</strong></article>
          <article><span>In review</span><strong>{stats.reviewing_reports}</strong></article>
          <article><span>Banned users</span><strong>{stats.banned_users}</strong></article>
          <article><span>Posts (24h)</span><strong>{stats.posts_24h}</strong></article>
          <article><span>Total users</span><strong>{stats.users_total}</strong></article>
          <article><span>Moderators</span><strong>{stats.moderators}</strong></article>
        </div>
      )}

      {tab === 'reports' && (
        <div className="admin-section">
          <div className="admin-toolbar">
            {(['open', 'reviewing', 'resolved', 'dismissed', 'all'] as const).map((status) => (
              <button key={status} type="button" className={reportFilter === status ? 'selected' : ''} onClick={() => setReportFilter(status)}>
                {status[0].toUpperCase() + status.slice(1)}
              </button>
            ))}
            <button type="button" className="secondary-button small" disabled={busy} onClick={() => void load('reports')}>Refresh</button>
          </div>
          <div className="admin-list">
            {reports.map((report) => (
              <article key={report.id} className="admin-card">
                <header>
                  <b>@{report.reporter_handle}</b>
                  <span> reported {report.reported_handle ? `@${report.reported_handle}` : report.post_id ? 'a post' : 'content'}</span>
                  <span className={`admin-pill status-${report.status}`}>{report.status}</span>
                </header>
                <p><strong>{report.reason}</strong>{report.details ? ` — ${report.details}` : ''}</p>
                {report.post_body && <blockquote>{report.post_body.slice(0, 280)}{report.post_body.length > 280 ? '…' : ''}</blockquote>}
                <small>{relative(report.created_at)}</small>
                <div className="admin-card-actions">
                  {report.reported_user_id && <button type="button" className="secondary-button small" onClick={() => onOpenProfile(report.reported_user_id!)}>Profile</button>}
                  {report.status === 'open' && (
                    <button type="button" className="secondary-button small" disabled={busy} onClick={() => void run(() => store.staffUpdateReport(report.id, 'reviewing'), 'Marked reviewing')}>Reviewing</button>
                  )}
                  {report.status !== 'resolved' && (
                    <button type="button" className="secondary-button small" disabled={busy} onClick={() => void run(() => store.staffUpdateReport(report.id, 'resolved', 'Handled by staff'), 'Report resolved')}>Resolve</button>
                  )}
                  {report.status !== 'dismissed' && (
                    <button type="button" className="secondary-button small" disabled={busy} onClick={() => void run(() => store.staffUpdateReport(report.id, 'dismissed', 'Dismissed'), 'Report dismissed')}>Dismiss</button>
                  )}
                  {report.post_id && (
                    <button type="button" className="secondary-button small danger-outline" disabled={busy} onClick={() => void run(async () => {
                      await store.staffMassDeletePosts([report.post_id!])
                      await store.staffUpdateReport(report.id, 'resolved', 'Post deleted by staff')
                    }, 'Post deleted and report resolved')}>Delete post</button>
                  )}
                  {report.reported_user_id && (
                    <button type="button" className="secondary-button small danger-outline" disabled={busy} onClick={() => {
                      const reason = window.prompt('Ban reason (optional)')
                      if (reason === null) return
                      void run(() => store.staffBanUser(report.reported_user_id!, reason), 'User banned')
                    }}>Ban user</button>
                  )}
                </div>
              </article>
            ))}
            {reports.length === 0 && !loading && <EmptyState icon={<Shield />} title="Report queue is clear" body="New user reports will show up here for staff review." />}
          </div>
        </div>
      )}

      {tab === 'banned' && (
        <div className="admin-section">
          <div className="admin-toolbar">
            <button type="button" className="secondary-button small" disabled={busy} onClick={() => void load('banned')}>Refresh</button>
          </div>
          <div className="admin-list">
            {banned.map((user) => (
              <article key={user.id} className="admin-card">
                <header>
                  <b>{user.display_name}</b>
                  <span>@{user.handle}</span>
                  <span className="admin-pill status-open">banned</span>
                </header>
                <p>{user.ban_reason || 'No reason recorded.'}</p>
                <small>Banned {relative(user.banned_at)} · {compact(user.follower_count ?? 0)} followers</small>
                <div className="admin-card-actions">
                  <button type="button" className="secondary-button small" onClick={() => onOpenProfile(user.id)}>Profile</button>
                  <button type="button" className="primary-button small" disabled={busy} onClick={() => void run(() => store.staffUnbanUser(user.id), 'User unbanned')}>Unban</button>
                </div>
              </article>
            ))}
            {banned.length === 0 && !loading && <EmptyState icon={<User />} title="No banned users" body="Accounts you ban will appear here." />}
          </div>
        </div>
      )}

      {tab === 'posts' && (
        <div className="admin-section">
          <div className="admin-toolbar">
            <button type="button" className="secondary-button small" disabled={busy} onClick={() => void load('posts')}>Refresh</button>
            <button
              type="button"
              className="secondary-button small"
              disabled={busy || posts.length === 0}
              onClick={() => setSelected(new Set(posts.map((item) => item.id)))}
            >
              Select all
            </button>
            <button type="button" className="secondary-button small" disabled={busy || selected.size === 0} onClick={() => setSelected(new Set())}>Clear</button>
            <button
              type="button"
              className="primary-button small danger-outline"
              disabled={busy || selected.size === 0}
              onClick={() => {
                if (!window.confirm(`Delete ${selected.size} post${selected.size === 1 ? '' : 's'}? This cannot be undone.`)) return
                void run(async () => { await store.staffMassDeletePosts([...selected]) }, `Deleted ${selected.size} post${selected.size === 1 ? '' : 's'}`)
              }}
            >
              Delete selected ({selected.size})
            </button>
          </div>
          <div className="admin-list">
            {posts.map((post) => (
              <label key={post.id} className={`admin-card admin-post-row ${selected.has(post.id) ? 'selected' : ''}`}>
                <input type="checkbox" checked={selected.has(post.id)} onChange={() => togglePost(post.id)} />
                <div>
                  <header>
                    <b>{post.author_name}</b>
                    <span>@{post.author_handle}</span>
                    <span className="admin-pill">{post.post_kind}</span>
                  </header>
                  <p>{post.body.slice(0, 220)}{post.body.length > 220 ? '…' : ''}</p>
                  <small>
                    {post.published_at ? relative(post.published_at) : '—'}
                    {' · '}
                    {compact(post.like_count)} likes · {compact(post.comment_count)} comments
                    {post.media_url ? ' · has image' : ''}
                  </small>
                </div>
              </label>
            ))}
            {posts.length === 0 && !loading && <EmptyState icon={<PenLine />} title="No recent posts" body="Published posts will appear here for mass moderation." />}
          </div>
        </div>
      )}
    </div>
  )
}

function ShortsViewer({
  shorts,
  authors,
  currentUserId,
  startId,
  onClose,
  onLike,
  onRepost,
  onComments,
  onProfile,
  onShare,
  onFollow,
  onMention,
  onHashtag,
}: {
  shorts: AppyCreation[]
  authors: Record<string, AppyUser>
  currentUserId: string
  startId?: string | null
  onClose: () => void
  onLike: (id: string) => void
  onRepost: (id: string) => void
  onComments: (id: string) => void
  onProfile: (userId: string) => void
  onShare: (app: AppyCreation) => void
  onFollow: (userId: string) => void
  onMention: (handle: string) => void
  onHashtag: (tag: string) => void
}) {
  const startIndex = Math.max(0, shorts.findIndex((item) => item.id === startId))
  const [index, setIndex] = useState(startIndex >= 0 ? startIndex : 0)
  const videoRef = useRef<HTMLVideoElement>(null)
  const current = shorts[index]
  const author = current ? (authors[current.authorId] ?? missingUser(current.authorId)) : null

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowDown' || event.key === 'j') setIndex((i) => Math.min(shorts.length - 1, i + 1))
      if (event.key === 'ArrowUp' || event.key === 'k') setIndex((i) => Math.max(0, i - 1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, shorts.length])

  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    el.currentTime = 0
    void el.play().catch(() => { /* autoplay may be blocked */ })
  }, [index, current?.id])

  if (!current || !author) {
    return (
      <div className="shorts-viewer empty">
        <button type="button" className="icon-button shorts-close" aria-label="Close Shorts" onClick={onClose}><X /></button>
        <EmptyState icon={<Video />} title="No Shorts yet" body="Upload a Short from Post — Luna reviews every video before it goes live." />
      </div>
    )
  }

  return (
    <div className="shorts-viewer" role="dialog" aria-modal="true" aria-label="Shorts">
      <button type="button" className="icon-button shorts-close" aria-label="Close Shorts" onClick={onClose}><X /></button>
      <div
        className="shorts-stage"
        onWheel={(event) => {
          if (Math.abs(event.deltaY) < 40) return
          if (event.deltaY > 0) setIndex((i) => Math.min(shorts.length - 1, i + 1))
          else setIndex((i) => Math.max(0, i - 1))
        }}
        onTouchStart={(event) => {
          const y = event.touches[0]?.clientY ?? 0
          ;(event.currentTarget as HTMLElement).dataset.touchY = String(y)
        }}
        onTouchEnd={(event) => {
          const start = Number((event.currentTarget as HTMLElement).dataset.touchY ?? 0)
          const end = event.changedTouches[0]?.clientY ?? start
          const dy = start - end
          if (Math.abs(dy) < 48) return
          if (dy > 0) setIndex((i) => Math.min(shorts.length - 1, i + 1))
          else setIndex((i) => Math.max(0, i - 1))
        }}
      >
        <video
          key={current.id}
          ref={videoRef}
          className="shorts-video"
          src={current.videoUrl ?? undefined}
          playsInline
          loop
          autoPlay
          controls={false}
          onClick={(event) => {
            const el = event.currentTarget
            if (el.paused) void el.play()
            else el.pause()
          }}
        />
        <div className="shorts-gradient" />
        <div className="shorts-meta">
          <button type="button" className="shorts-author" onClick={() => onProfile(author.id)}>
            <UserAvatar user={author} size="sm" />
            <div>
              <b>{author.name}</b>
              <span>@{author.handle}</span>
            </div>
          </button>
          {author.id !== currentUserId && (
            <button type="button" className={`follow-inline ${author.followedByMe ? 'following' : ''}`} onClick={() => onFollow(author.id)}>
              {author.followedByMe ? 'Following' : 'Follow'}
            </button>
          )}
          <p className="shorts-caption">{renderRichText(current.postBody || current.description, { onMention, onHashtag })}</p>
        </div>
        <div className="shorts-actions">
          <button type="button" className={current.likedByMe ? 'liked' : ''} onClick={() => onLike(current.id)} aria-label="Like"><Heart fill={current.likedByMe ? 'currentColor' : 'none'} /><span>{compact(current.likes)}</span></button>
          <button type="button" onClick={() => onComments(current.id)} aria-label="Comments"><MessageCircle /><span>{compact(current.comments)}</span></button>
          <button type="button" className={current.repostedByMe ? 'reposted' : ''} onClick={() => onRepost(current.id)} aria-label="Repost"><Repeat2 /><span>{compact(current.reposts)}</span></button>
          <button type="button" onClick={() => onShare(current)} aria-label="Share"><Share2 /></button>
        </div>
        <div className="shorts-nav-hint" aria-hidden="true">
          <button type="button" className="icon-button" disabled={index <= 0} onClick={() => setIndex((i) => Math.max(0, i - 1))} aria-label="Previous Short">↑</button>
          <span>{index + 1}/{shorts.length}</span>
          <button type="button" className="icon-button" disabled={index >= shorts.length - 1} onClick={() => setIndex((i) => Math.min(shorts.length - 1, i + 1))} aria-label="Next Short">↓</button>
        </div>
      </div>
    </div>
  )
}

export default function App() {
  const store = useAppyStore()
  const { actionError, clearActionError, snapshot, currentUser, runtimeDelete, runtimeGet, runtimePublicProfile, runtimeSet } = store
  const [view, setView] = useState<View>('home')
  const [feedMode, setFeedMode] = useState<FeedMode>('for-you')
  const [feedFilter, setFeedFilter] = useState<'all' | 'app' | 'game' | 'short'>('all')
  const [composer, setComposer] = useState(false)
  const [shortsStartId, setShortsStartId] = useState<string | null>(null)
  const [playerId, setPlayerId] = useState<string | null>(() => new URLSearchParams(window.location.search).get('app'))
  const [commentsId, setCommentsId] = useState<string | null>(null)
  const [editorId, setEditorId] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [mobileMenu, setMobileMenu] = useState(false)
  const [notificationFilter, setNotificationFilter] = useState<NotificationFilter>('all')
  const [libraryTab, setLibraryTab] = useState<LibraryTab>('saved')
  const [buildTab, setBuildTab] = useState<BuildTab>('all')
  const [profileTab, setProfileTab] = useState<ProfileTab>('posts')
  const [profileEditor, setProfileEditor] = useState(false)
  const [infoModal, setInfoModal] = useState<InfoKind | null>(null)
  const [accountOpen, setAccountOpen] = useState(false)
  const [accountMode, setAccountMode] = useState<'signup' | 'signin'>('signup')
  const [viewedProfileId, setViewedProfileId] = useState<string | null>(null)
  const [editPostId, setEditPostId] = useState<string | null>(null)
  const [editPostBody, setEditPostBody] = useState('')
  const [feedSignals, setFeedSignals] = useState<{
    followedIds: string[]
    likedAuthors: Record<string, number>
    commentedAuthors: Record<string, number>
    dwellByPost: Record<string, number>
  }>({ followedIds: [], likedAuthors: {}, commentedAuthors: {}, dwellByPost: {} })
  const [sessionDwell, setSessionDwell] = useState<Record<string, number>>({})
  const buildingApps = useMemo(
    () => snapshot.apps.filter((app) => app.authorId === snapshot.currentUserId && app.status === 'building' && app.postType !== 'text' && app.postType !== 'short'),
    [snapshot.apps, snapshot.currentUserId],
  )
  const activeBuild = buildingApps[0] ?? null

  useEffect(() => {
    if (!actionError) return
    setToast(actionError)
    clearActionError()
  }, [actionError, clearActionError])

  // Only when closing/refreshing the browser tab — never for in-app navigation.
  // Refresh also cancels orphaned builds on the next load (cancel_my_active_builds).
  useEffect(() => {
    if (!activeBuild) return
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [activeBuild])

  useEffect(() => {
    if (store.loading || store.loadError || snapshot.account.status === 'anonymous') return
    void appyAdapter.loadFeedSignals?.().then((signals) => {
      if (signals) setFeedSignals(signals)
    }).catch(() => { /* ranking falls back to engagement */ })
  }, [store.loading, store.loadError, snapshot.account.status, snapshot.currentUserId])

  useEffect(() => {
    if (store.loading || store.loadError || !playerId || snapshot.apps.some((app) => app.id === playerId)) return
    const next = new URL(window.location.href)
    next.searchParams.delete('app')
    window.history.replaceState({}, '', next)
    setPlayerId(null)
    setToast('This app is unavailable or private.')
  }, [playerId, snapshot.apps, store.loadError, store.loading])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.target as HTMLElement)?.matches('input, textarea')) return
      if (event.key.toLowerCase() === 'n') setComposer(true)
      if (event.key === '/') { event.preventDefault(); setView('explore'); window.setTimeout(() => document.querySelector<HTMLInputElement>('#appy-search')?.focus(), 50) }
    }
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    const handleRuntime = (event: MessageEvent) => {
      if (!event.data || event.data.source !== 'appy-runtime') return
      const frame = [...document.querySelectorAll<HTMLIFrameElement>('iframe[data-appy-app-id]')].find((item) => item.contentWindow === event.source)
      const appId = frame?.dataset.appyAppId
      if (!appId) return
      if (event.data.appId && event.data.appId !== appId) return
      const source = event.source as WindowProxy | null
      const requestId = typeof event.data.requestId === 'string' ? event.data.requestId : undefined
      const reply = (payload: { value?: unknown; error?: string }) => {
        if (requestId) source?.postMessage({ source: 'appy-host', requestId, ...payload }, '*')
      }
      void (async () => {
        try {
          if (event.data.type === 'user:publicProfile') {
            reply({ value: await runtimePublicProfile(appId) })
            return
          }
          const safeKey = String(event.data.key ?? '').slice(0, 120)
          if (!/^[a-zA-Z0-9_.:-]{1,120}$/.test(safeKey)) throw new Error('Invalid Appy storage key')
          if (event.data.type === 'storage:set') { await runtimeSet(appId, safeKey, event.data.value); reply({ value: true }); return }
          if (event.data.type === 'storage:delete') { await runtimeDelete(appId, safeKey); reply({ value: true }); return }
          if (event.data.type === 'storage:get') reply({ value: await runtimeGet(appId, safeKey) })
        } catch (error) {
          reply({ error: error instanceof Error ? error.message : 'App storage is unavailable' })
        }
      })()
    }
    window.addEventListener('message', handleRuntime); return () => window.removeEventListener('message', handleRuntime)
  }, [runtimeDelete, runtimeGet, runtimePublicProfile, runtimeSet])

  const authors = useMemo(() => Object.fromEntries(snapshot.users.map((user) => [user.id, user])), [snapshot.users])
  const published = useMemo(() => snapshot.apps.filter((app) => app.status === 'published'), [snapshot.apps])
  const trendingTopics = useMemo(() => rankTrendingTopics(published, 6), [published])
  const trendingApps = useMemo(() => rankTrendingApps(published, 40), [published])
  const whoToFollow = useMemo(() => rankWhoToFollow(snapshot.users, published, snapshot.currentUserId, 5), [snapshot.users, published, snapshot.currentUserId])
  const exploreCreators = useMemo(() => rankWhoToFollow(snapshot.users, published, snapshot.currentUserId, 12), [snapshot.users, published, snapshot.currentUserId])
  const topicFilters = useMemo(() => {
    const fromTags = trendingTopics.map((topic) => topic.label.replace(/^#/, ''))
    const base = ['All', 'Apps', 'Games', 'Shorts', ...fromTags]
    return [...new Set(base)].slice(0, 10)
  }, [trendingTopics])
  const followedIds = useMemo(() => {
    const fromUsers = snapshot.users.filter((user) => user.followedByMe).map((user) => user.id)
    return new Set([...fromUsers, ...feedSignals.followedIds])
  }, [snapshot.users, feedSignals.followedIds])
  const planFeatures = useMemo(() => featuresForPlan(snapshot.billing.plan), [snapshot.billing.plan])
  const homeApps = useMemo(() => {
    const filtered = published
      .filter((app) => feedMode === 'for-you' || followedIds.has(app.authorId))
      .filter((app) => {
        if (feedFilter === 'all') return true
        if (feedFilter === 'short') return app.postType === 'short'
        if (app.postType === 'text' || app.postType === 'short') return false
        return app.kind === feedFilter
      })
    return rankPersonalizedFeed(filtered, {
      followedIds,
      likedAuthors: feedSignals.likedAuthors,
      commentedAuthors: feedSignals.commentedAuthors,
      dwellByPost: feedSignals.dwellByPost,
      sessionDwellByPost: sessionDwell,
    })
  }, [published, feedMode, followedIds, feedFilter, feedSignals, sessionDwell])
  const shortsFeed = useMemo(() => rankShortsFeed(published, {
    followedIds,
    likedAuthors: feedSignals.likedAuthors,
    commentedAuthors: feedSignals.commentedAuthors,
    dwellByPost: feedSignals.dwellByPost,
    sessionDwellByPost: sessionDwell,
  }), [published, followedIds, feedSignals, sessionDwell])
  const searchNeedle = query.trim().toLowerCase()
  const isUserSearch = searchNeedle.startsWith('@') || searchNeedle.startsWith('user:')
  const userSearchTerm = isUserSearch
    ? searchNeedle.replace(/^@/, '').replace(/^user:/, '').trim()
    : ''
  const searchUsers = useMemo(() => {
    if (!isUserSearch) return [] as AppyUser[]
    const term = userSearchTerm
    return snapshot.users
      .filter((user) => {
        if (user.id === snapshot.currentUserId) return false
        if (isGuestLike(user)) return false
        if (!term) return true
        return user.handle.toLowerCase().includes(term)
          || user.name.toLowerCase().includes(term)
      })
      .sort((a, b) => {
        const aExact = a.handle.toLowerCase() === term ? 0 : a.handle.toLowerCase().startsWith(term) ? 1 : 2
        const bExact = b.handle.toLowerCase() === term ? 0 : b.handle.toLowerCase().startsWith(term) ? 1 : 2
        if (aExact !== bExact) return aExact - bExact
        return b.followers - a.followers || a.handle.localeCompare(b.handle)
      })
      .slice(0, 24)
  }, [isUserSearch, userSearchTerm, snapshot.users, snapshot.currentUserId])
  const searchApps = useMemo(() => {
    const needle = searchNeedle
    if (isUserSearch) {
      if (!userSearchTerm) return []
      const matchedIds = new Set(searchUsers.map((user) => user.id))
      // Also match posts that mention @handle
      return trendingApps.filter((app) => {
        if (matchedIds.has(app.authorId)) return true
        const blob = `${app.postBody ?? ''} ${app.description}`.toLowerCase()
        return blob.includes(`@${userSearchTerm}`)
      })
    }
    const pool = (() => {
      if (!needle || needle === 'all') return trendingApps
      if (needle === 'apps') return trendingApps.filter((app) => app.kind === 'app' && app.postType !== 'text' && app.postType !== 'short')
      if (needle === 'games') return trendingApps.filter((app) => app.kind === 'game' && app.postType !== 'text' && app.postType !== 'short')
      if (needle === 'shorts' || needle === 'short') return trendingApps.filter((app) => app.postType === 'short')
      return trendingApps.filter((app) => `${app.title} ${app.description} ${app.postBody ?? ''} ${app.tags.join(' ')} ${app.kind}`.toLowerCase().includes(needle.replace(/^#/, '')))
    })()
    return pool
  }, [searchNeedle, isUserSearch, userSearchTerm, searchUsers, trendingApps])
  const filteredNotifications = snapshot.notifications.filter((item) => {
    if (notificationFilter === 'all') return true
    if (notificationFilter === 'builds') return item.type === 'build'
    if (notificationFilter === 'social') return ['like', 'comment', 'repost', 'follow', 'mention', 'reply'].includes(item.type)
    return item.type === 'system' || item.type === 'verification'
  })
  const meStaff = currentUser.staffRole === 'owner' || currentUser.staffRole === 'moderator'
  const meOwner = currentUser.staffRole === 'owner'
  const libraryApps = libraryTab === 'saved'
    ? snapshot.apps.filter((app) => app.savedByMe)
    : libraryTab === 'recent'
      ? [...snapshot.apps].filter((app) => app.plays > 0).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 12)
      : libraryTab === 'created'
        ? snapshot.apps.filter((app) => app.authorId === snapshot.currentUserId)
        : []
  const ownBuilds = snapshot.apps.filter((app) => app.authorId === snapshot.currentUserId && app.postType !== 'text' && app.postType !== 'short' && (buildTab === 'all' || app.status === buildTab))
  const profileUser = snapshot.users.find((user) => user.id === (viewedProfileId ?? snapshot.currentUserId)) ?? currentUser
  const isOwnProfile = profileUser.id === snapshot.currentUserId
  const profileApps = profileTab === 'posts'
    ? snapshot.apps.filter((app) => app.authorId === profileUser.id && app.status === 'published' && (app.postType === 'text' || app.postType === 'short'))
    : profileTab === 'apps'
      ? snapshot.apps.filter((app) => app.authorId === profileUser.id && app.status === 'published' && app.postType !== 'text' && app.postType !== 'short')
      : profileTab === 'reposts'
        ? published.filter((app) => app.repostedByMe && isOwnProfile)
        : profileTab === 'likes'
          ? published.filter((app) => app.likedByMe && isOwnProfile)
          : []
  const player = snapshot.apps.find((app) => app.id === playerId)
  const commentApp = snapshot.apps.find((app) => app.id === commentsId)
  const editorApp = snapshot.apps.find((app) => app.id === editorId && app.authorId === snapshot.currentUserId)
  const editingPost = snapshot.apps.find((app) => app.id === editPostId)

  if (store.loading) return <LoadingScreen />
  if (store.loadError) return <ConnectionError detail={store.loadError} onRetry={store.retryLoad} />
  if (!snapshot.onboarded) return <Onboarding onComplete={store.completeOnboarding} />

  const nav: { id: View; label: string; icon: React.ReactNode; badge?: number }[] = [
    { id: 'home', label: 'Home', icon: <Home /> },
    { id: 'explore', label: 'Explore', icon: <Search /> },
    { id: 'shorts', label: 'Shorts', icon: <Video /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell />, badge: snapshot.notifications.filter((item) => !item.read).length },
    { id: 'library', label: 'Library', icon: <Library /> },
    { id: 'builds', label: 'Builds', icon: <Layers3 /> },
    { id: 'profile', label: 'Profile', icon: <User /> },
    { id: 'settings', label: 'Settings', icon: <Settings /> },
    ...(meStaff ? [{ id: 'admin' as const, label: 'Admin', icon: <Shield />, badge: undefined as number | undefined }] : []),
  ]
  const selectView = (id: View) => {
    setView(id)
    setMobileMenu(false)
    if (id !== 'profile') setViewedProfileId(null)
    window.scrollTo({ top: 0, behavior: snapshot.settings.reduceMotion ? 'auto' : 'smooth' })
  }
  const openHashtag = (tag: string) => {
    setQuery(tag.startsWith('#') ? tag.slice(1) : tag)
    selectView('explore')
  }
  const openShorts = (id?: string) => {
    setShortsStartId(id ?? null)
    setView('shorts')
    setMobileMenu(false)
  }
  const openProfile = (userId: string) => { setViewedProfileId(userId); setProfileTab('posts'); setView('profile'); setMobileMenu(false); window.scrollTo({ top: 0, behavior: 'smooth' }) }
  const openHandle = (handle: string) => {
    const user = snapshot.users.find((item) => item.handle.toLowerCase() === handle.toLowerCase())
    if (user) openProfile(user.id)
    else {
      setQuery(`@${handle}`)
      selectView('explore')
      setToast(`Looking up @${handle}`)
    }
  }
  const openPlayer = (id: string) => { const next = new URL(window.location.href); next.searchParams.set('app', id); window.history.replaceState({}, '', next); setPlayerId(id) }
  const closePlayer = () => { const next = new URL(window.location.href); next.searchParams.delete('app'); window.history.replaceState({}, '', next); setPlayerId(null) }
  const share = async (app: AppyCreation) => { try { await navigator.clipboard.writeText(`${location.origin}/?app=${encodeURIComponent(app.id)}`); setToast('Link copied') } catch { setToast('Could not copy the link') } }
  const build = async (prompt: string, kind: AppKind, intelligence: Intelligence) => { store.setIntelligence(intelligence); await store.build({ prompt, kind, intelligence }); setView('builds'); setToast(`${kind === 'game' ? 'Game' : 'App'} build started — open Builds to watch live progress`) }
  const card = (app: AppyCreation) => <AppCard key={app.id} app={app} author={authors[app.authorId] ?? missingUser(app.authorId)} currentUserId={snapshot.currentUserId} onLike={() => store.toggleLike(app.id)} onSave={() => { store.toggleSave(app.id); setToast(app.savedByMe ? 'Removed from Library' : 'Saved to Library') }} onRepost={() => store.toggleRepost(app.id)} onOpen={() => { if (app.postType === 'text') { setCommentsId(app.id); return } if (app.postType === 'short') { openShorts(app.id); return } if (app.status !== 'building') { store.recordPlay(app.id); openPlayer(app.id) } }} onOpenShort={() => openShorts(app.id)} onComments={() => setCommentsId(app.id)} onFollow={() => store.toggleFollow(app.authorId)} onEditor={() => setEditorId(app.id)} onShare={() => void share(app)} onProfile={() => openProfile(app.authorId)} onMention={openHandle} onHashtag={openHashtag} onDwell={(dwellMs, opened) => {
    const key = app.postId ?? app.id
    setSessionDwell((current) => ({ ...current, [key]: (current[key] ?? 0) + dwellMs }))
    if (app.postId) store.recordImpression(app.postId, dwellMs, feedMode === 'following' ? 'following' : 'for_you', opened)
  }} onEditPost={app.postId && app.authorId === snapshot.currentUserId ? () => { setEditPostId(app.id); setEditPostBody(app.postBody || app.description || '') } : undefined} onDeletePost={app.postId && (app.authorId === snapshot.currentUserId || meStaff) ? () => { void store.deletePost(app.id).then(() => setToast(app.authorId === snapshot.currentUserId ? 'Post deleted' : 'Post removed by staff')).catch((error: unknown) => setToast(errorText(error, 'Could not delete post'))) } : undefined} onReport={app.authorId !== snapshot.currentUserId && snapshot.account.status === 'secured' ? () => { const reason = window.prompt('Report reason: spam, unsafe, copyright, harassment, illegal, or other', 'spam'); if (!reason) return; const details = window.prompt('Details (optional)') ?? ''; void store.reportContent({ reason: reason.trim().toLowerCase(), details, postId: app.postId, projectId: app.postType === 'text' || app.postType === 'short' ? undefined : app.id, reportedUserId: app.authorId }).then(() => setToast('Report submitted')).catch((error: unknown) => setToast(errorText(error, 'Could not submit report'))) } : undefined} />

  let content: React.ReactNode
  if (view === 'home') content = <><header className="feed-header"><div className="feed-tabs"><button className={feedMode === 'for-you' ? 'selected' : ''} onClick={() => setFeedMode('for-you')}>For You</button><button className={feedMode === 'following' ? 'selected' : ''} onClick={() => setFeedMode('following')}>Following</button></div><div className="feed-filters">{(['all', 'app', 'game', 'short'] as const).map((item) => <button key={item} className={feedFilter === item ? 'selected' : ''} onClick={() => setFeedFilter(item)}>{item === 'all' ? 'All' : item === 'short' ? 'Shorts' : `${item}s`}</button>)}</div></header>{store.mode === 'supabase' && snapshot.account.status !== 'secured' && <div className="account-banner" role="status"><User /><div><b>{snapshot.account.status === 'verification-pending' ? 'Confirm your email' : 'Create an Appy account'}</b><span>{snapshot.account.status === 'verification-pending' ? 'Billing and account recovery unlock after you verify your email.' : 'Save your apps, buy credits, and sign in on other devices.'}</span></div><button className="primary-button small" onClick={() => { setAccountMode(snapshot.account.status === 'verification-pending' ? 'signin' : 'signup'); setAccountOpen(true) }}>{snapshot.account.status === 'verification-pending' ? 'Account' : 'Create account'}</button></div>}<div className="quick-compose"><UserAvatar user={currentUser} /><button onClick={() => setComposer(true)}>What do you want to build?</button><button className="spark-button" onClick={() => setComposer(true)}><Sparkles />Post</button></div><div className="feed-list">{homeApps.map(card)}{homeApps.length === 0 && <EmptyState icon={feedMode === 'following' ? <User /> : <Sparkles />} title={feedMode === 'following' ? 'Your Following feed is quiet' : 'No posts yet'} body={feedMode === 'following' ? 'Follow creators from Explore to see their newest apps here.' : 'Appy just started — the feed is empty until someone publishes. Be the first to post.'} action={feedMode === 'following' ? 'Explore creators' : 'Post'} onAction={() => feedMode === 'following' ? selectView('explore') : setComposer(true)} />}</div></>
  else if (view === 'shorts') content = <ShortsViewer shorts={shortsFeed} authors={authors} currentUserId={snapshot.currentUserId} startId={shortsStartId} onClose={() => selectView('home')} onLike={(id) => store.toggleLike(id)} onRepost={(id) => store.toggleRepost(id)} onComments={(id) => setCommentsId(id)} onProfile={openProfile} onShare={(app) => void share(app)} onFollow={(userId) => store.toggleFollow(userId)} onMention={openHandle} onHashtag={openHashtag} />
  else if (view === 'explore') content = <div className="page-view"><header className="search-header"><Search /><label className="sr-only" htmlFor="appy-search">Search Appy</label><input id="appy-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search apps, #tags, or @username" />{query && <button aria-label="Clear search" onClick={() => setQuery('')}><X /></button>}</header>{topicFilters.length > 0 && !isUserSearch && <div className="category-strip">{topicFilters.map((item) => <button key={item} className={(query.trim() || 'All').toLowerCase() === item.toLowerCase() || (!query.trim() && item === 'All') ? 'selected' : ''} onClick={() => setQuery(item === 'All' ? '' : item)}>{item}</button>)}</div>}{!query.trim() && <section className="explore-hero"><span>{published.length > 0 ? 'Live on Appy' : 'Brand new network'}</span><h1>Small apps.<br />Big rabbit holes.</h1><p>{published.length > 0 ? 'Ranked by plays, likes, reposts, saves, and comments from real posts. Type @ to find people.' : 'Nothing is published yet — be the first to post an app or game.'}</p><button onClick={() => setComposer(true)}>Make an app<Sparkles /></button></section>}{isUserSearch ? <><h2 className="section-title">{userSearchTerm ? `People matching “@${userSearchTerm}”` : 'Search people with @'}</h2><div className="creator-grid explore-user-results">{searchUsers.map((user) => <article key={user.id}><button type="button" className="explore-user-hit" onClick={() => openProfile(user.id)}><UserAvatar user={user} size="lg" /><h3>{user.name}<UserBadges user={user} /></h3><span>@{user.handle}</span><p>{user.bio || `${compact(user.followers)} followers`}</p></button><button className={user.followedByMe ? 'following' : ''} onClick={() => store.toggleFollow(user.id)}>{user.followedByMe ? 'Following' : 'Follow'}</button></article>)}{searchUsers.length === 0 && <EmptyState icon={<User />} title={userSearchTerm ? 'No users found' : 'Type a username'} body={userSearchTerm ? `Nobody matches @${userSearchTerm} yet.` : 'Try @handle to find creators on Appy.'} />}</div>{searchApps.length > 0 && <><h2 className="section-title">Posts from or mentioning them</h2><div className="feed-list">{searchApps.map(card)}</div></>}</> : <><h2 className="section-title">{query.trim() ? `Results for “${query.trim()}”` : 'Trending now'}</h2><div className="feed-list">{searchApps.map(card)}{searchApps.length === 0 && <EmptyState icon={<Search />} title={query.trim() ? 'No matches' : 'Nothing trending yet'} body={query.trim() ? 'Try a different search, @username, or post the app you wish existed.' : 'Trending fills in when people publish and others play, like, and repost.'} action="Post" onAction={() => setComposer(true)} />}</div><h2 className="section-title">Creators to follow</h2><div className="creator-grid">{exploreCreators.map(({ user, plays, likes, published: publishedCount }) => <article key={user.id}><UserAvatar user={user} size="lg" /><h3>{user.name}<UserBadges user={user} /></h3><span>@{user.handle}</span><p>{user.bio || `${publishedCount} published · ${compact(plays)} plays · ${compact(likes)} likes`}</p><small>{compact(user.followers)} followers</small><button className={user.followedByMe ? 'following' : ''} onClick={() => store.toggleFollow(user.id)}>{user.followedByMe ? 'Following' : 'Follow'}</button></article>)}{exploreCreators.length === 0 && <EmptyState icon={<User />} title="No creators to follow yet" body="When people join and publish, Appy ranks them by followers, plays, likes, and reposts. Search @username anytime." />}</div></>}</div>
  else if (view === 'notifications') content = <div className="page-view"><header className="page-header inline"><div><h1>Notifications</h1><p>Builds, social activity, mentions, and updates.</p></div><button onClick={store.markNotificationsRead}>Mark all read</button></header><div className="notification-filters">{([['all', 'All'], ['builds', 'Builds'], ['social', 'Social'], ['updates', 'Updates']] as const).map(([id, label]) => <button key={id} className={notificationFilter === id ? 'selected' : ''} aria-pressed={notificationFilter === id} onClick={() => setNotificationFilter(id)}>{label}</button>)}</div><div className="notification-list">{filteredNotifications.map((item) => <button className={!item.read ? 'unread' : ''} key={item.id} onClick={() => { store.markNotificationRead(item.id); if (item.type === 'mention' || item.type === 'comment' || item.type === 'reply' || item.type === 'like' || item.type === 'repost') { if (item.appId && snapshot.apps.some((app) => app.id === item.appId)) setCommentsId(item.appId); else if (item.userId) openProfile(item.userId) } else if (item.appId) { const target = snapshot.apps.find((app) => app.id === item.appId); if (target?.authorId === snapshot.currentUserId) setEditorId(item.appId); else if (target) setCommentsId(item.appId) } else if (item.userId) openProfile(item.userId) }}><span className={`notification-icon ${item.type}`}>{item.type === 'build' ? <Sparkles /> : item.type === 'like' ? <Heart /> : item.type === 'follow' ? <User /> : item.type === 'mention' || item.type === 'reply' ? <MessageCircle /> : item.type === 'verification' ? <Shield /> : <Bell />}</span><div><b>{item.title}</b><p>{item.body}</p><small>{relative(item.createdAt)}</small></div>{!item.read && <i />}</button>)}{filteredNotifications.length === 0 && <EmptyState icon={<Bell />} title="Nothing here yet" body="New activity in this category will appear here." />}</div></div>
  else if (view === 'library') content = <div className="page-view"><header className="page-header"><h1>App Library</h1><p>Saved apps, games, and progress — ready whenever you are.</p></header><div className="library-tabs">{([['saved', 'Saved'], ['recent', 'Recently played'], ['created', 'Created'], ['collections', 'Collections']] as const).map(([id, label]) => <button key={id} className={libraryTab === id ? 'selected' : ''} aria-pressed={libraryTab === id} onClick={() => setLibraryTab(id)}>{label}</button>)}</div><div className="library-grid">{libraryApps.map((app) => <article key={app.id}><button aria-label={`Open ${app.title}`} onClick={() => { store.recordPlay(app.id); openPlayer(app.id) }}><AppPreview app={app} /><span className="library-open"><Play />Open</span></button><div><h3>{app.title}</h3><p>by @{(authors[app.authorId] ?? missingUser(app.authorId)).handle}</p><small>v{app.version} · Progress saved</small>{app.savedByMe && <button className="icon-button" aria-label={`Remove ${app.title} from Library`} onClick={() => store.toggleSave(app.id)}><MoreHorizontal /></button>}</div></article>)}</div>{libraryApps.length === 0 && <EmptyState icon={libraryTab === 'collections' ? <Layers3 /> : <Bookmark />} title={libraryTab === 'collections' ? 'Collections are ready for you' : 'No apps in this view'} body={libraryTab === 'collections' ? 'Save a few apps, then organize them into collections.' : 'Open Explore to find something worth keeping.'} action="Explore apps" onAction={() => selectView('explore')} />}</div>
  else if (view === 'builds') content = <div className="page-view"><header className="page-header inline"><div><h1>Builds</h1><p>Drafts, active builds, versions, and published apps.</p></div><button className="primary-button small" onClick={() => setComposer(true)}><Plus />New build</button></header><div className="build-tabs">{(['all', 'building', 'ready', 'published'] as const).map((id) => <button key={id} className={buildTab === id ? 'selected' : ''} aria-pressed={buildTab === id} onClick={() => setBuildTab(id)}>{id[0].toUpperCase() + id.slice(1)}</button>)}</div><div className="build-grid">{ownBuilds.map((app) => <article key={app.id}><AppPreview app={app} /><div className="build-card-content"><span className={`status-pill ${app.status}`}>{app.status === 'building' ? `Building ${app.kind === 'game' ? 'Game' : 'App'}` : app.status}</span><h3>{app.title}</h3><p>{app.description}</p><small>{app.status === 'building' ? (app.buildLog?.at(-1)?.message || 'Building…') : `Version ${app.version} · Updated ${relative(app.updatedAt)}`}</small><div className="build-card-actions"><button onClick={() => setEditorId(app.id)}>{app.status === 'ready' ? 'Review and Post' : app.status === 'building' ? 'View progress' : 'Open builder'}<ChevronRight /></button>{app.status === 'building' && <button type="button" className="secondary-button small danger-outline" onClick={() => void store.cancelBuild(app.id).then(() => setToast('Build stopped')).catch((error: unknown) => setToast(errorText(error, 'Could not stop build')))}>Stop</button>}</div></div></article>)}</div>{ownBuilds.length === 0 && <EmptyState icon={<Layers3 />} title={buildTab === 'all' ? 'Your builds will appear here' : `No ${buildTab} builds`} body="Start with a prompt. Appy keeps the draft even if you leave." action="Post" onAction={() => setComposer(true)} />}</div>
  else if (view === 'profile') content = <div className="page-view profile-page"><div className="profile-banner">{profileUser.bannerUrl ? <img src={profileUser.bannerUrl} alt="" className="banner-image" /> : <div className="banner-pattern" />}{!isOwnProfile && <button type="button" className="profile-back" onClick={() => openProfile(snapshot.currentUserId)}>← Back to you</button>}</div><section className="profile-head"><UserAvatar user={profileUser} size="lg" />{isOwnProfile ? (snapshot.account.status === 'anonymous' ? <button type="button" className="primary-button small profile-action" onClick={() => { setAccountMode('signup'); setAccountOpen(true) }}>Create account</button> : <button type="button" className="secondary-button small profile-action" onClick={() => setProfileEditor(true)}>Edit profile</button>) : <button type="button" className={`profile-action ${profileUser.followedByMe ? 'secondary-button small following-pill' : 'primary-button small'}`} onClick={() => store.toggleFollow(profileUser.id)}>{profileUser.followedByMe ? 'Following' : 'Follow'}</button>}<h1>{profileUser.name}<UserBadges user={profileUser} /></h1><span>@{profileUser.handle}</span><p>{isOwnProfile && snapshot.account.status === 'anonymous' ? 'You are browsing as a guest — not signed in. Create an account to keep apps, credits, and this profile.' : (profileUser.bio || 'No bio yet.')}</p><div><b>{compact(profileUser.following)}</b> Following <b>{compact(profileUser.followers)}</b> Followers</div></section>{!isOwnProfile && meStaff && <div className="staff-controls"><span className="staff-controls-label">Staff tools</span>{profileUser.banned ? <button type="button" className="secondary-button small" onClick={() => void store.staffUnbanUser(profileUser.id).then(() => setToast('User unbanned')).catch((error: unknown) => setToast(errorText(error, 'Could not unban')))}>Unban</button> : <button type="button" className="secondary-button small danger-outline" onClick={() => { const reason = window.prompt('Ban reason (optional)'); if (reason === null) return; void store.staffBanUser(profileUser.id, reason).then(() => setToast('User banned')).catch((error: unknown) => setToast(errorText(error, 'Could not ban'))) }}>Ban</button>}{meOwner && profileUser.staffRole !== 'owner' && <button type="button" className="secondary-button small" onClick={() => void store.staffSetModerator(profileUser.id, profileUser.staffRole !== 'moderator').then(() => setToast(profileUser.staffRole === 'moderator' ? 'Moderator removed' : 'Made moderator')).catch((error: unknown) => setToast(errorText(error, 'Could not update moderator')))}>{profileUser.staffRole === 'moderator' ? 'Remove moderator' : 'Make moderator'}</button>}</div>}{profileUser.banned && <p className="banned-banner" role="status">This account is banned{profileUser.banReason ? `: ${profileUser.banReason}` : ''}.</p>}<div className="profile-tabs">{(isOwnProfile ? (['posts', 'apps', 'reposts', 'replies', 'likes'] as const) : (['posts', 'apps'] as const)).map((id) => <button key={id} className={profileTab === id ? 'selected' : ''} aria-pressed={profileTab === id} onClick={() => setProfileTab(id)}>{id[0].toUpperCase() + id.slice(1)}</button>)}</div><div className="feed-list">{profileApps.map(card)}{profileApps.length === 0 && <EmptyState icon={profileTab === 'replies' ? <MessageCircle /> : profileTab === 'posts' ? <PenLine /> : <AppWindow />} title={profileTab === 'posts' ? (isOwnProfile ? 'No posts yet' : 'No posts yet') : profileTab === 'apps' ? (isOwnProfile ? 'No apps yet' : 'No apps yet') : `No ${profileTab} yet`} body={profileTab === 'posts' ? (isOwnProfile ? 'Write a normal post (with optional image) and it will show up here.' : 'This creator has not written any posts yet.') : profileTab === 'apps' ? (isOwnProfile ? 'Publish an app or game to fill this tab.' : 'This creator has not published any apps yet.') : 'Your activity will appear here.'} action={isOwnProfile && (profileTab === 'posts' || profileTab === 'apps') ? (profileTab === 'posts' ? 'New post' : 'Post') : undefined} onAction={isOwnProfile && (profileTab === 'posts' || profileTab === 'apps') ? () => setComposer(true) : undefined} />}</div></div>
  else if (view === 'admin' && meStaff) content = <AdminView store={store} onToast={setToast} onOpenProfile={openProfile} onOpenSettings={() => selectView('settings')} />
  else if (view === 'settings') content = <SettingsView store={store} onToast={setToast} onOpenLegal={(kind) => setInfoModal(kind)} />
  else content = <BillingView store={store} onToast={setToast} onOpenAccount={() => { setAccountMode(snapshot.account.status === 'anonymous' ? 'signup' : 'signin'); setAccountOpen(true) }} />

  return <div className={`appy-shell theme-${snapshot.settings.theme} ${snapshot.settings.reduceMotion ? 'reduce-motion' : ''} ${mobileMenu ? 'mobile-menu-open' : ''}`}>
    {mobileMenu && <button type="button" className="mobile-nav-scrim" aria-label="Close menu" onClick={() => setMobileMenu(false)} />}
    <aside id="primary-navigation" className={`left-nav ${mobileMenu ? 'mobile-open' : ''}`}><div className="brand-row"><Brand /><button type="button" className="icon-button nav-refresh" aria-label="Refresh Appy" title="Refresh without stopping builds" disabled={store.refreshing} onClick={() => { void store.softRefresh().then((ok) => setToast(ok ? 'Refreshed' : 'Could not refresh')) }}><RefreshCw className={store.refreshing ? 'spin' : undefined} /></button></div><nav aria-label="Primary">{nav.map((item) => <button key={item.id} className={view === item.id ? 'selected' : ''} aria-label={item.label} aria-current={view === item.id ? 'page' : undefined} onClick={() => selectView(item.id)}>{item.icon}<span>{item.label}</span>{Boolean(item.badge) && <b>{item.badge}</b>}</button>)}</nav><button className="post-app-button" aria-label="Post" onClick={() => setComposer(true)}><PenLine /><span>Post</span></button><div className="nav-bottom"><button className="credits-mini" aria-label="Manage build credits" onClick={() => selectView('billing')}><Zap /><div><b>{formatCredits(snapshot.billing)}</b><span>{snapshot.billing.unlimited ? 'unlimited credits' : 'build credits'}</span></div><ChevronRight /></button>{store.mode === 'supabase' && snapshot.account.status !== 'secured' && <button className="nav-account-cta" aria-label="Create account or sign in" onClick={() => { setAccountMode('signup'); setAccountOpen(true) }}><User /><span>{snapshot.account.status === 'verification-pending' ? 'Verify email' : 'Create account'}</span></button>}<button className="account-mini" aria-label={snapshot.account.status === 'anonymous' ? 'Guest — create account' : 'Open profile'} onClick={() => { if (snapshot.account.status === 'anonymous') { setAccountMode('signup'); setAccountOpen(true) } else selectView('profile') }}><UserAvatar user={currentUser} size="sm" /><div><b>{snapshot.account.status === 'anonymous' ? 'Guest' : currentUser.name}</b><span>{snapshot.account.status === 'anonymous' ? 'Not signed in' : snapshot.account.status === 'secured' ? `@${currentUser.handle}` : snapshot.account.email ?? `@${currentUser.handle}`}</span></div></button></div></aside>
    <main className="center-column">
      <header className="mobile-header">
        <button
          type="button"
          className="mobile-menu-button"
          aria-label={mobileMenu ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileMenu}
          aria-controls="primary-navigation"
          onClick={() => setMobileMenu((open) => !open)}
        >
          {mobileMenu ? <X /> : <Menu />}
        </button>
        <Brand small />
        <div className="mobile-header-actions">
          <button type="button" className="mobile-header-icon" aria-label="Refresh Appy" title="Refresh without stopping builds" disabled={store.refreshing} onClick={() => { void store.softRefresh().then((ok) => setToast(ok ? 'Refreshed' : 'Could not refresh')) }}><RefreshCw className={store.refreshing ? 'spin' : undefined} /></button>
          <button type="button" className="mobile-header-credits" aria-label="Manage build credits" onClick={() => selectView('billing')}>{formatCredits(snapshot.billing, 0)}</button>
        </div>
      </header>
      {content}
    </main>
    <aside className="right-rail"><label className="rail-search"><Search /><span className="sr-only">Search Appy</span><input placeholder="Search Appy" onFocus={() => selectView('explore')} /></label><section className="rail-credit"><div><span>Build credits</span><strong>{formatCredits(snapshot.billing)}</strong></div><div className="rail-progress"><i style={{ width: `${snapshot.billing.unlimited ? 100 : Math.min(100, snapshot.billing.creditBalance / Math.max(1, snapshot.billing.creditAllowance) * 100)}%` }} /></div><button onClick={() => selectView('billing')}>Manage credits<ChevronRight /></button></section><section className="daily-card"><div><Sparkles /><span>Daily Game</span><button className={`switch ${snapshot.settings.dailyGame ? 'on' : ''}`} role="switch" aria-label="Daily Game" aria-checked={snapshot.settings.dailyGame} onClick={() => { void store.configureDailyGame({ enabled: !snapshot.settings.dailyGame, hour: snapshot.settings.dailyGameHour, intelligence: snapshot.settings.dailyGameIntelligence }).then(() => setToast(snapshot.settings.dailyGame ? 'Daily Game paused' : 'Daily Game enabled')).catch((error: unknown) => setToast(errorText(error, 'Could not update Daily Game'))) }}><i /></button></div><p>Generate and auto-post one game daily using your credits.</p><small>{snapshot.settings.dailyGame ? `Tomorrow at ${formatHour(snapshot.settings.dailyGameHour)} · charged by usage` : 'Off by default'}</small></section><section className="rail-section"><h2>Trending</h2>{trendingTopics.length === 0 ? <p className="rail-empty">Nothing trending yet. Tags appear here from real published apps.</p> : trendingTopics.map((topic, index) => <button key={topic.key} onClick={() => { setQuery(topic.label.replace(/^#/, '')); selectView('explore') }}><span>{index + 1} · Trending</span><b>{topic.label}</b><small>{compact(topic.apps)} app{topic.apps === 1 ? '' : 's'}</small></button>)}</section><section className="rail-section"><h2>Who to follow</h2>{whoToFollow.length === 0 ? <p className="rail-empty">No creators to suggest yet. Rankings use followers, plays, likes, and reposts.</p> : whoToFollow.map(({ user, plays, likes }) => <div className="rail-user" key={user.id}><UserAvatar user={user} size="sm" /><div><b>{user.name}</b><span>@{user.handle} · {compact(user.followers)} followers</span>{(plays > 0 || likes > 0) && <small className="rail-user-meta">{compact(plays)} plays · {compact(likes)} likes</small>}</div><button onClick={() => store.toggleFollow(user.id)}>Follow</button></div>)}</section><footer><button onClick={() => setInfoModal('terms')}>Terms</button><button onClick={() => setInfoModal('privacy')}>Privacy</button><button onClick={() => setInfoModal('help')}>Help</button><span>© 2026 Starex</span></footer></aside>
    <nav className="mobile-bottom" aria-label="Primary mobile">{nav.slice(0, 4).map((item, index) => index === 2 ? <button className="mobile-post" key="post" aria-label="Post" onClick={() => setComposer(true)}><Plus /></button> : <button key={item.id} className={view === item.id ? 'selected' : ''} aria-label={item.label} aria-current={view === item.id ? 'page' : undefined} onClick={() => selectView(item.id)}>{item.icon}</button>)}</nav>
    {composer && <Composer intelligence={snapshot.settings.intelligence} plan={planFeatures} onClose={() => setComposer(false)} onBuild={build} onUploadPostMedia={async (file) => store.uploadMedia('post', file, undefined, { maxBytes: planFeatures.maxImageBytes })} onUploadShortMedia={async (file) => store.uploadMedia('short', file, undefined, { maxBytes: planFeatures.maxVideoBytes })} onModerateImage={async (file) => store.moderatePostImage(file, { maxBytes: planFeatures.maxImageBytes })} onModerateVideo={async (file) => store.moderatePostVideo(file, { maxBytes: planFeatures.maxVideoBytes, maxSeconds: planFeatures.maxShortSeconds })} onTextPost={async (body, mediaUrl) => { await store.createTextPost(body, mediaUrl); setToast('Posted'); setView('home') }} onShortPost={async (body, videoUrl, durationSeconds) => { await store.createShortPost(body, videoUrl, durationSeconds); setToast('Short posted'); openShorts() }} />}
    {commentApp && <CommentsModal app={commentApp} comments={snapshot.comments} users={snapshot.users} currentUser={currentUser} onClose={() => setCommentsId(null)} onAdd={(body, parentId) => store.addComment(commentApp.id, body, parentId)} onLike={store.toggleCommentLike} onRepost={store.toggleCommentRepost} onProfile={openProfile} onOpenHandle={openHandle} />}
    {player && <Player app={player} saved={player.savedByMe} onClose={closePlayer} onSave={() => store.toggleSave(player.id)} />}
    {editorApp && <Editor app={editorApp} onClose={() => setEditorId(null)} onRevise={(instruction) => store.revise(editorApp.id, instruction)} onStopBuild={async () => { await store.cancelBuild(editorApp.id); setToast('Build stopped') }} onPublish={(caption) => { store.publish(editorApp.id, caption); setToast(editorApp.status === 'published' ? 'Post updated' : 'Posted with your caption'); setEditorId(null); openProfile(snapshot.currentUserId) }} onRestore={async (version) => { const restored = await store.restoreVersion(editorApp.id, version); setToast(`Restored version ${version} as version ${restored}`); return restored }} onSetCover={async (file) => { const url = await store.uploadMedia('cover', file, editorApp.id); await store.setProjectCover(editorApp.id, url); setToast('Cover image updated') }} />}
    {profileEditor && <ProfileEditor user={currentUser} onClose={() => setProfileEditor(false)} onUpload={(kind, file) => store.uploadMedia(kind, file)} onSave={async (profile) => { await store.updateProfile(profile); setToast('Profile saved') }} />}
    {editingPost && <Modal onClose={() => setEditPostId(null)} label="Edit post"><form className="form-modal" onSubmit={(event) => { event.preventDefault(); if (countWords(editPostBody) > planFeatures.maxPostWords) { setToast(`${planFeatures.label} allows up to ${planFeatures.maxPostWords.toLocaleString()} words`); return } void store.updatePost(editingPost.id, editPostBody).then(() => { setToast('Post updated'); setEditPostId(null) }).catch((error: unknown) => setToast(errorText(error, 'Could not update post'))) }}><header><div><span className="eyebrow">Edit your words · {planFeatures.label}</span><h2>Edit post</h2></div><button type="button" className="icon-button" aria-label="Close edit post" onClick={() => setEditPostId(null)}><X /></button></header><label>Post text<textarea value={editPostBody} onChange={(event) => setEditPostBody(event.target.value)} maxLength={planFeatures.maxPostChars} required /><small>{countWords(editPostBody).toLocaleString()} / {planFeatures.maxPostWords.toLocaleString()} words</small></label><footer><button type="button" className="secondary-button" onClick={() => setEditPostId(null)}>Cancel</button><button className="primary-button" disabled={!editPostBody.trim()}>Save</button></footer></form></Modal>}
    {infoModal && <InfoModal kind={infoModal} onClose={() => setInfoModal(null)} onBuild={() => setComposer(true)} />}
    {accountOpen && <AccountModal store={store} initialMode={accountMode} onClose={() => setAccountOpen(false)} />}
    {toast && <Toast text={toast} onClose={() => setToast(null)} />}
  </div>
}
